import React, { createContext, useContext, useEffect, useState } from 'react';
import appConfig from './config';
import { getVisibleTabs, ALL_TABS } from './roles';

const API_BASE = appConfig.apiBaseUrl;

const TABS = [
  { id: 'briefing', label: 'Briefing Room' },
  { id: 'calendar', label: 'Calendar' },
  { id: 'todos', label: 'To-Do List' },
  { id: 'loans', label: 'Active Loans' },
  { id: 'loanboard', label: 'Loan Board' },
  { id: 'deadlines', label: 'Deadlines & Tasks' },
  { id: 'pipeline', label: 'Pipeline' },
  { id: 'goals', label: 'Goals' },
  { id: 'projects', label: 'Projects' },
  { id: 'wisehire', label: 'WiseHire' },
  { id: 'analytics', label: 'Analytics' },
];

// Role ids are snake_case (see roles.js), and acronyms like CEO don't
// survive automatic capitalisation (`capitalize` + a naive replace turns
// "ceo" into "Ceo" and "loan_officer_processor" into "Loan
// officer_processor"), so each role gets an explicit display label here.
const ROLE_LABELS = {
  admin: 'Admin',
  ceo: 'CEO',
  loan_officer: 'Loan Officer',
  processor: 'Processor',
  loan_officer_processor: 'Loan Officer / Processor',
};

const DashboardContext = createContext({ refreshKey: 0 });

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatCurrency(amount) {
  const n = Number(amount) || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

function formatDate(value) {
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

// Arive sends plain "YYYY-MM-DD" dates for most fields but full ISO
// timestamps for rate lock / lock expiry. `new Date("2026-08-13")` parses
// a plain date as UTC midnight, which renders as the previous day in US
// Eastern local time. parseLoanDate treats a plain date string as a local
// calendar date instead of a UTC instant; full timestamps still parse
// normally since they already carry an explicit instant.
function parseLoanDate(value) {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

function daysUntil(value) {
  const d = parseLoanDate(value);
  if (!d) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(d);
  target.setHours(0, 0, 0, 0);
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round((target.getTime() - today.getTime()) / msPerDay);
}

function formatTime(value) {
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function formatDateTime(value) {
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}, ${d.toLocaleTimeString(
    undefined,
    { hour: 'numeric', minute: '2-digit' }
  )}`;
}

function getWeekDays(anchor) {
  const start = new Date(anchor);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - start.getDay());
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

// ---------------------------------------------------------------------------
// Calendar-event date/time helpers
// ---------------------------------------------------------------------------
//
// A calendar event carries an `allDay` boolean (Microsoft Graph's
// isAllDay, mapped through by the backend). Graph represents an all-day
// event as midnight-to-midnight in the event's own timezone; the backend
// stores that as if it were a UTC instant (see backend/services/graph.js's
// toIsoUtc), so a stored startTime like 2026-09-04T00:00:00.000Z encodes
// the intended calendar date -- Sep 4 -- in its UTC year/month/day, NOT a
// literal UTC instant to convert. Reading it through `new Date(...)` and
// then local-time accessors (exactly what isSameDay/formatTime/
// formatDateTime do) lands on the wrong day and shows a nonsense time
// (8pm the day before, in US Eastern) -- the bug these helpers exist to
// avoid. A timed event's startTime IS a real UTC instant with no such
// caveat, so every helper below leaves a timed event's behaviour
// untouched and only special-cases ev.allDay === true.

// The Date to use whenever an event needs to be grouped, filtered,
// range-checked, or sorted by day. For a timed event this is just
// `new Date(ev.startTime)`, unchanged. For an all-day event it's local
// midnight built from startTime's UTC year/month/day -- the same "read
// the calendar date directly, don't convert it through a timezone" move
// parseLoanDate makes for plain YYYY-MM-DD loan dates (parseLoanDate
// itself isn't reused here -- its regex only matches a bare date, not a
// full timestamp). Every other date in this file an event gets compared
// against (today, startOfTomorrow, endOfWeek, getWeekDays' entries) is
// itself a local-midnight-anchored Date, so this keeps an all-day
// event's comparison value on the same footing as theirs.
function eventCompareDate(ev) {
  const d = new Date(ev.startTime);
  if (isNaN(d.getTime())) return null;
  if (!ev.allDay) return d;
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

// Is this event on the given day? `day` is a plain local calendar-date
// Date (e.g. "today", or one of getWeekDays' entries) -- never itself an
// event. Reuses isSameDay unchanged, just fed the corrected comparison
// date from eventCompareDate instead of a raw `new Date(ev.startTime)`.
function isEventOnDay(ev, day) {
  const d = eventCompareDate(ev);
  return d ? isSameDay(d, day) : false;
}

// The time badge shown next to an event. An all-day event has no real
// clock time to show -- Graph's own midnight timestamp isn't one -- so
// showing one (e.g. "8:00 PM") is the same bug in a different form.
function formatEventTime(ev) {
  return ev.allDay ? 'All day' : formatTime(ev.startTime);
}

// The combined date+time label used where an event's day isn't already
// implied by its surrounding grouping (This Week's Attention, the To-Do
// tab's calendar card). An all-day event shows just its corrected
// calendar date, no time, built the same way eventCompareDate does.
function formatEventDateTime(ev) {
  if (!ev.allDay) return formatDateTime(ev.startTime);
  const d = eventCompareDate(ev);
  if (!d) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// ---------------------------------------------------------------------------
// Data fetching
// ---------------------------------------------------------------------------

async function apiFetch(path) {
  let res;
  try {
    res = await fetch(`${API_BASE}${path}`);
  } catch {
    throw new Error('Cannot reach the backend. Make sure the server is running on localhost:3001.');
  }

  let json = null;
  try {
    json = await res.json();
  } catch {
    // no JSON body — handled by the status check below
  }

  if (!res.ok || (json && json.success === false)) {
    throw new Error((json && json.error) || `Request failed with status ${res.status}`);
  }

  return json && json.data !== undefined ? json.data : json;
}

// POST/PATCH/DELETE counterpart to apiFetch above (which is GET-only).
// Mirrors its exact error handling, so a write failure surfaces the
// backend's own error message (e.g. a 403 role message) rather than a
// generic one.
async function apiWrite(path, method, body) {
  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new Error('Cannot reach the backend. Make sure the server is running on localhost:3001.');
  }

  let json = null;
  try {
    json = await res.json();
  } catch {
    // no JSON body — handled by the status check below
  }

  if (!res.ok || (json && json.success === false)) {
    throw new Error((json && json.error) || `Request failed with status ${res.status}`);
  }

  return json && json.data !== undefined ? json.data : json;
}

function useApiData(path) {
  const { refreshKey } = useContext(DashboardContext);
  const [retryTick, setRetryTick] = useState(0);
  const [state, setState] = useState({ data: null, loading: true, error: null });

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true, error: null }));

    apiFetch(path)
      .then((data) => {
        if (!cancelled) setState({ data, loading: false, error: null });
      })
      .catch((err) => {
        if (!cancelled) setState({ data: null, loading: false, error: err.message });
      });

    return () => {
      cancelled = true;
    };
  }, [path, refreshKey, retryTick]);

  return { ...state, retry: () => setRetryTick((t) => t + 1) };
}

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function RefreshIcon({ spinning }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={`h-5 w-5 ${spinning ? 'animate-spin' : ''}`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 4v5h5M20 20v-5h-5M4.5 9a7.5 7.5 0 0113-4.5M19.5 15a7.5 7.5 0 01-13 4.5"
      />
    </svg>
  );
}

function MenuIcon({ open }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5">
      {open ? (
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      ) : (
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
      )}
    </svg>
  );
}

function WarningIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-8 w-8 text-red-400 mb-3">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v4m0 4h.01M10.29 3.86l-8.18 14.18A2 2 0 004 21h16a2 2 0 001.89-2.96L13.71 3.86a2 2 0 00-3.42 0z"
      />
    </svg>
  );
}

function ChartIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-8 w-8 text-gold">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 19V10M10 19V5M16 19v-7M4 19h16" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Shared UI primitives
// ---------------------------------------------------------------------------

function Card({ children, className = '' }) {
  return (
    <div className={`bg-zinc-950 border border-zinc-800 rounded-xl shadow-lg shadow-black/40 ${className}`}>
      {children}
    </div>
  );
}

function SectionHeading({ children, right }) {
  return (
    <div className="flex items-center justify-between mb-4 gap-3">
      <h2 className="text-xs font-semibold tracking-[0.2em] text-gold uppercase">{children}</h2>
      {right}
    </div>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <Card className="p-5">
      <p className="text-xs uppercase tracking-wider text-zinc-500">{label}</p>
      <p className="text-3xl font-bold text-white mt-1">{value}</p>
      {sub && <p className="text-xs text-zinc-500 mt-1">{sub}</p>}
    </Card>
  );
}

function LoadingBlock({ label = 'Loading data...' }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-zinc-400">
      <div className="h-10 w-10 rounded-full border-2 border-gold/30 border-t-gold animate-spin mb-4" />
      <p className="text-sm tracking-wide">{label}</p>
    </div>
  );
}

function ErrorBlock({ message, onRetry }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center border border-red-900/40 bg-red-950/10 rounded-xl">
      <WarningIcon />
      <p className="text-red-300 font-medium mb-1">Unable to load data</p>
      <p className="text-zinc-500 text-sm max-w-sm mb-4">{message}</p>
      <button
        onClick={onRetry}
        className="px-4 py-2 rounded-lg border border-gold/40 text-gold hover:bg-gold/10 transition text-sm font-medium"
      >
        Try Again
      </button>
    </div>
  );
}

function EmptyBlock({ message }) {
  return <div className="py-12 text-center text-zinc-500 text-sm">{message}</div>;
}

function StatusBadge({ status }) {
  const s = (status || '').toLowerCase();
  const styles = {
    closed: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
    approved: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
    denied: 'bg-red-500/10 text-red-400 border-red-500/30',
    underwriting: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
    processing: 'bg-sky-500/10 text-sky-400 border-sky-500/30',
    // Same emerald used above for closed/approved (and for a complete
    // tracker in TRACKER_STATE_STYLES) -- the broker check being received
    // is the "paid" milestone within Closed/Funded, so it gets the same
    // "done" colour rather than the default gray every other raw enum
    // status falls back to.
    broker_check_received: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
  };
  const cls = styles[s] || 'bg-zinc-500/10 text-zinc-300 border-zinc-500/30';
  return (
    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium border capitalize whitespace-nowrap ${cls}`}>
      {status || 'pipeline'}
    </span>
  );
}

const TH_CLASS = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-400 whitespace-nowrap';
const TD_CLASS = 'px-4 py-3 whitespace-nowrap';

function getLoanDateValue(loan, dateName) {
  const entry = ((loan && loan.dates) || []).find((d) => d.dateName === dateName);
  return entry ? entry.dateValue : null;
}

function formatDateWithDays(rawValue) {
  const d = parseLoanDate(rawValue);
  if (!d) return { label: '—', days: null };
  const days = daysUntil(rawValue);
  const daysLabel = days < 0 ? `${Math.abs(days)}d overdue` : `${days}d`;
  return { label: `${formatDate(d)} (${daysLabel})`, days };
}

// Plain "just the date, no days-until" rendering for the detail panel --
// still routed through parseLoanDate/formatDate so plain YYYY-MM-DD values
// keep reading as local calendar dates rather than UTC midnight.
function formatPlainDate(rawValue) {
  const d = parseLoanDate(rawValue);
  return d ? formatDate(d) : '—';
}

// 7 and 14 day thresholds are a starting point to be tuned with the
// processor's feedback.
function lockExpiryUrgencyClass(days) {
  if (days === null) return 'text-zinc-300';
  if (days <= 7) return 'text-red-400';
  if (days <= 14) return 'text-amber-400';
  return 'text-zinc-300';
}

// stage is written once, at webhook time, by resolveMilestone in
// handlers.js -- a loan that hasn't received a webhook since that backend
// humanizing shipped can still hold a raw enum (e.g. APPLICATION_INTAKE)
// instead of the readable label every other row shows. Formatting here,
// at render time, means the cell is correct regardless of what's actually
// stored, and stays correct if the backend derivation changes again later.
//
// Mirrors resolveMilestone's own transform exactly (split on underscores,
// lowercase, capitalize each word) -- but only when the value looks like
// a raw enum rather than an already-humanized label. Two signals, either
// one is enough to trigger the transform:
//   - it contains an underscore (APPLICATION_INTAKE, BROKER_CHECK_RECEIVED)
//   - it's entirely uppercase (PREAPPROVED, ADVERSE, SUSPENDED,
//     QUALIFICATION -- single-word enums with no underscore to catch on)
// A raw Arive enum is always uppercase, with or without underscores, by
// definition; an already-humanized label never is -- "CD Sent",
// "Appraisal In Progress", "Title In Progress", and "Funding" all contain
// a lowercase letter, so the uppercase check rules them out too, even
// "CD Sent", which has no underscore either.
//
// The uppercase check requires at least one actual letter first --
// comparing a string to its own uppercased form is trivially true for a
// string with no letters at all (e.g. "—", or a plain number like "123"),
// which would otherwise be treated as an enum and run through the
// split/capitalize transform for no reason.
function formatMilestone(stage) {
  if (typeof stage !== 'string' || !stage) return '—';

  const hasUnderscore = stage.includes('_');
  const hasLetter = /[a-zA-Z]/.test(stage);
  const isAllUppercase = hasLetter && stage === stage.toUpperCase();

  if (!hasUnderscore && !isAllUppercase) return stage;

  return stage
    .split('_')
    .filter(Boolean)
    .map((word) => word[0].toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

// The Milestone shown for a loan is derived from the exact same
// PIPELINE_STAGES mapping that decides which Pipeline section the loan
// sits in (via findPipelineStage below, which mirrors resolveLoanStage's
// own match) -- not from the stored stage field resolveMilestone writes
// in handlers.js at webhook time. resolveMilestone tops out at "CD Sent"
// (see its own comment), so a loan that's advanced past that point --
// Clear to Close, Final Review, Conditionally Approved, anything later --
// would otherwise show a stale tracker-derived label that has nothing to
// do with the section it's actually in. Deriving the label from the same
// mapping that determines the section means the two can't drift apart:
// there is no separate list to keep in sync, only one source of truth.
// resolveMilestone still runs and stage is still written and still read
// elsewhere; this column just stops being one of its readers.
//
// Two kinds of loan fall back to their own (humanized) status instead of
// a section label:
//   - Closed/Funded. That one section deliberately groups three outcomes
//     that are NOT interchangeable -- funded, broker check received,
//     commission paid -- so showing "Closed/Funded" for all three would
//     erase a distinction that matters. This is the one stage where the
//     status is more informative than the section it lives in.
//   - Adverse, Suspended, and anything landing in Other. ADVERSE and
//     SUSPENDED are excluded from PIPELINE_STAGES entirely (see
//     PIPELINE_EXCLUDED_STATUSES) -- they never reach the Pipeline tab,
//     but this same LoanTable is shared by Active Loans and the Loan
//     Board, which do show them, so they still need a milestone that
//     isn't "—". Other means no stage matched at all -- the raw status
//     is the only thing known about that loan either way.
function resolveMilestoneLabel(loan) {
  const status = (loan.status || '').toUpperCase().trim();
  if (status === 'ADVERSE' || status === 'SUSPENDED') return loan.status;

  const stage = findPipelineStage(loan.status);
  if (!stage) return loan.status;
  if (stage.id === 'CLOSED_FUNDED') return loan.status;

  return stage.label;
}

function LoanTable({
  loans,
  showTrackers = false,
  showTeam = false,
  showMonthMove = false,
  monthOptions = [],
  onMonthMoved = () => {},
}) {
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  if (loans.length === 0) return <EmptyBlock message="No loans to display." />;

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // The Month column only actually renders for a writable role (see
  // NOTES_WRITABLE_ROLES) -- everyone else sees nothing in that column,
  // not a disabled control, so this single flag gates the header cell,
  // colSpan, and each row's cell identically. showMonthMove alone (e.g.
  // true but a read-only role) must not reserve a column nobody sees.
  const canMoveMonth = showMonthMove && NOTES_WRITABLE_ROLES.includes(appConfig.user.role);

  // Derived from the same flags that control the optional header columns
  // above, rather than a hardcoded number, so the detail row's colSpan
  // never drifts out of sync with however many columns are actually
  // rendered.
  const columnCount =
    1 + // borrower
    (showTeam ? 2 : 0) + // loan officer, processor
    1 + // loan number
    1 + // amount
    1 + // milestone
    (showTrackers ? 3 : 0) + // trackers, closing, lock expiry
    1 + // loan type
    1 + // status
    (showTrackers ? 1 : 0) + // chevron toggle column
    (canMoveMonth ? 1 : 0); // month move column

  return (
    <div className="overflow-x-auto rounded-xl border border-zinc-800">
      <table className="min-w-full divide-y divide-zinc-800 text-sm">
        <thead className="bg-zinc-900">
          <tr>
            {showTrackers && <th className={`${TH_CLASS} w-8`} aria-hidden="true" />}
            <th className={TH_CLASS}>Borrower</th>
            {showTeam && <th className={TH_CLASS}>Loan Officer</th>}
            {showTeam && <th className={TH_CLASS}>Processor</th>}
            <th className={TH_CLASS}>Loan #</th>
            <th className={TH_CLASS}>Amount</th>
            <th className={TH_CLASS}>Milestone</th>
            {showTrackers && <th className={TH_CLASS}>Trackers</th>}
            {showTrackers && <th className={TH_CLASS}>Closing</th>}
            {showTrackers && <th className={TH_CLASS}>Lock Expiry</th>}
            <th className={TH_CLASS}>Loan Type</th>
            <th className={TH_CLASS}>Status</th>
            {canMoveMonth && <th className={TH_CLASS}>Month</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-800 bg-black">
          {loans.map((l) => {
            // Shows the date Arive itself displays as the closing date
            // (its loan header's "Est Closing") -- the Deadlines tab's
            // stale-file alert (makeClosingDatePassedRule) reads this same
            // CLOSING_CONTINGENCY field, so the two surfaces agree.
            // EARLIEST_CLOSING is a regulatory floor, not a working
            // target; it's still visible in the detail panel's Key Dates.
            const closing = formatDateWithDays(getLoanDateValue(l, 'CLOSING_CONTINGENCY'));
            const lockExpiry = formatDateWithDays(getLoanDateValue(l, 'LOCK_EXPIRY'));
            const isExpanded = showTrackers && expandedIds.has(l.id);
            return (
              <React.Fragment key={l.id}>
                <tr className="hover:bg-zinc-900/60 transition">
                  {showTrackers && (
                    <td className={`${TD_CLASS} w-8`}>
                      <button
                        type="button"
                        onClick={() => toggleExpanded(l.id)}
                        aria-expanded={isExpanded}
                        aria-label={`${isExpanded ? 'Collapse' : 'Expand'} details for ${l.borrowerName || 'this loan'}`}
                        className="flex items-center justify-center h-6 w-6 rounded text-zinc-400 hover:text-gold hover:bg-zinc-800 transition"
                      >
                        <svg
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          className={`h-3.5 w-3.5 transition-transform duration-150 ${isExpanded ? 'rotate-90' : ''}`}
                        >
                          <path
                            fillRule="evenodd"
                            d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </td>
                  )}
                  <td className={`${TD_CLASS} font-medium text-zinc-100`}>{l.borrowerName || '—'}</td>
                  {showTeam && (
                    <td className={`${TD_CLASS} text-zinc-400`}>{l.loanOfficerName || l.loanOfficerEmail || '—'}</td>
                  )}
                  {showTeam && (
                    <td className={`${TD_CLASS} text-zinc-400`}>
                      {l.loanProcessorName || l.loanProcessorEmail || '—'}
                    </td>
                  )}
                  <td className={`${TD_CLASS} text-zinc-400`}>{l.loanNumber}</td>
                  <td className={`${TD_CLASS} text-gold font-semibold`}>{formatCurrency(l.loanAmount)}</td>
                  <td className={`${TD_CLASS} text-zinc-300`}>{formatMilestone(resolveMilestoneLabel(l))}</td>
                  {showTrackers && (
                    <td className={TD_CLASS}>
                      <TrackerStrip loan={l} />
                    </td>
                  )}
                  {showTrackers && <td className={`${TD_CLASS} text-zinc-300`}>{closing.label}</td>}
                  {showTrackers && (
                    <td className={`${TD_CLASS} ${lockExpiryUrgencyClass(lockExpiry.days)}`}>{lockExpiry.label}</td>
                  )}
                  <td className={`${TD_CLASS} text-zinc-400`}>{l.loanType || '—'}</td>
                  <td className={TD_CLASS}>
                    <StatusBadge status={l.status} />
                  </td>
                  {canMoveMonth && (
                    <td className={TD_CLASS}>
                      <LoanMonthMoveControl loan={l} monthOptions={monthOptions} onMonthMoved={onMonthMoved} />
                    </td>
                  )}
                </tr>
                {isExpanded && (
                  <tr className="bg-zinc-950/60">
                    <td className={`${TD_CLASS} align-top`} colSpan={columnCount}>
                      <LoanDetailPanel loan={l} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Sentinel value for the "clear this override" option below -- distinct
// from any real "YYYY-MM" month key, so the select's onChange can tell
// the two apart without a second control.
const CLEAR_MONTH_OVERRIDE_VALUE = '__clear__';

// Per-row month-move control for LoanTable's Month column. Reads
// loan.pipelineMonthKey (the loan's current month, override-first, same
// value PipelineTab computed via monthKeyForLoan) and
// loan.pipelineMonthOverride (null unless a saved override exists) off
// the loan object itself -- both are attached by PipelineTab before the
// loan ever reaches this table, so this control needs no override map of
// its own, just the two props LoanTable already threads through.
function LoanMonthMoveControl({ loan, monthOptions, onMonthMoved }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const currentValue = loan.pipelineMonthKey || '';

  const handleChange = async (e) => {
    const value = e.target.value;
    if (!value || value === currentValue) return;

    setSaving(true);
    setError(null);
    try {
      if (value === CLEAR_MONTH_OVERRIDE_VALUE) {
        await apiWrite(`/month-overrides/${loan.ariveLoanId}`, 'DELETE');
      } else {
        await apiWrite(`/month-overrides/${loan.ariveLoanId}`, 'PUT', { month: value });
      }
      // Refetch the overrides, not just the loans -- the override map is
      // what determines grouping, and the loan data itself didn't change.
      onMonthMoved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <select
        value={currentValue}
        onChange={handleChange}
        disabled={saving}
        className="bg-zinc-900 border border-zinc-800 rounded-md px-2 py-1 text-xs text-zinc-200 focus:border-gold outline-none disabled:opacity-40"
      >
        {!currentValue && <option value="">— Select month —</option>}
        {monthOptions.map((monthKey) => (
          <option key={monthKey} value={monthKey}>
            {formatMonthLabel(monthKey)}
          </option>
        ))}
        {loan.pipelineMonthOverride && <option value={CLEAR_MONTH_OVERRIDE_VALUE}>Clear (use closing date)</option>}
      </select>
      {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
    </div>
  );
}

// Full display labels for each tracker -- TRACKER_ABBR's short codes are
// sized for the strip, but the detail panel below has room to spell them
// out.
const TRACKER_FULL_LABEL = {
  APPRAISAL: 'Appraisal',
  TITLE: 'Title',
  HOI: 'HOI',
  CD: 'CD',
  FUNDING_WIRE: 'Funding Wire',
  LE: 'LE',
};

// Named milestone dates (see ARIVE_MILESTONE_DATES in backend/handlers.js)
// shown alongside each tracker's own status/date in the detail panel, as
// [label, dateName] pairs in display order. FUNDING_WIRE has no named
// dates of its own -- just its tracker status/date -- so its list is
// empty; LE has no tracker entry at all, so only its dates apply.
const TRACKER_DETAIL_DATES = {
  // APPRAISAL_DELIVERY is mapped in the backend (see ARIVE_MILESTONE_DATES
  // in handlers.js) but arrives from Arive as an empty field on every
  // tenant -- zero populated rows anywhere -- so the "Delivered" line was
  // removed here rather than the mapping itself. If Arive starts sending
  // it, adding the line back is a one-line change.
  APPRAISAL: [['Ordered', 'APPRAISAL_ORDERED']],
  TITLE: [
    ['Ordered', 'TITLE_ORDERED'],
    ['Received', 'TITLE_RECEIVED'],
  ],
  HOI: [
    ['Ordered', 'HOI_ORDERED'],
    ['Received', 'HOI_RECEIVED'],
  ],
  CD: [
    ['Sent', 'MOST_RECENT_CD_SENT'],
    ['Signed', 'MOST_RECENT_CD_SIGNED'],
  ],
  FUNDING_WIRE: [],
  LE: [
    ['Sent', 'MOST_RECENT_LE_SENT'],
    ['Signed', 'MOST_RECENT_LE_SIGNED'],
  ],
};

// Section Two of the detail panel: standalone key dates that aren't tied
// to any one tracker.
const LOAN_KEY_DATES = [
  ['Earliest Closing', 'EARLIEST_CLOSING'],
  ['Lock Expiry', 'LOCK_EXPIRY'],
  ['Rate Lock', 'RATE_LOCK'],
  ['Loan Contingency', 'LOAN_CONTINGENCY'],
  ['Appraisal Contingency', 'APPRAISAL_CONTINGENCY'],
  ['Closing Contingency', 'CLOSING_CONTINGENCY'],
  ['Credit Expiration', 'CREDIT_EXPIRATION'],
];

function LoanDetailPanel({ loan }) {
  const trackerList = (loan && loan.trackers) || [];
  return (
    <div className="py-2 max-w-3xl">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">Trackers</p>
        <ul className="space-y-2">
          {TRACKER_ORDER.map((name) => {
            let state;
            let statusLabel;
            let trackerDateLabel = null;
            if (name === 'LE') {
              const le = classifyLeState(loan);
              state = le.state;
              statusLabel = le.state === 'not_started' ? 'not started' : le.state.replace('_', ' ');
            } else {
              const entry = trackerList.find((t) => t.trackerName === name);
              const rawStatus = (entry && entry.status) || '';
              state = classifyTrackerStatus(rawStatus);
              statusLabel = rawStatus || 'not started';
              trackerDateLabel = formatPlainDate(entry && entry.trackerDate);
            }
            const dateEntries = TRACKER_DETAIL_DATES[name] || [];
            return (
              <li key={name} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
                <span className="w-28 shrink-0 text-zinc-300">{TRACKER_FULL_LABEL[name]}</span>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-medium border capitalize ${TRACKER_STATE_STYLES[state]}`}
                >
                  {statusLabel}
                </span>
                {trackerDateLabel !== null && (
                  <span className="text-xs text-zinc-500">Tracker date: {trackerDateLabel}</span>
                )}
                {dateEntries.map(([label, dateName]) => (
                  <span key={dateName} className="text-xs text-zinc-500">
                    {label}: {formatPlainDate(getLoanDateValue(loan, dateName))}
                  </span>
                ))}
              </li>
            );
          })}
        </ul>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">Key Dates</p>
        <ul className="space-y-1.5">
          {LOAN_KEY_DATES.map(([label, dateName]) => (
            <li key={dateName} className="flex items-baseline gap-3 text-sm">
              <span className="w-40 shrink-0 text-zinc-400">{label}</span>
              <span className="text-zinc-200">{formatPlainDate(getLoanDateValue(loan, dateName))}</span>
            </li>
          ))}
        </ul>
      </div>
      </div>
      <div className="mt-6">
        <LoanNotesSection loan={loan} />
      </div>
    </div>
  );
}

// Writable roles for the shared loan notes thread. The backend
// (notes-worker.js, reached via each tenant's /api/notes proxy in
// worker.js) is the authoritative enforcement -- it returns 403 for any
// other role. This constant exists only so the UI doesn't show write
// controls that would just fail; it is not itself a security boundary.
const NOTES_WRITABLE_ROLES = ['processor', 'loan_officer_processor'];

// One note in the thread. Read-only (body + "author · when written",
// plus "edited by" when applicable) unless canWrite, in which case an
// Edit/Delete pair appears -- never rendered disabled for a read-only
// viewer, since a control nobody can use is worse than no control.
function NoteRow({ note, canWrite, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.body);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  const startEdit = () => {
    setDraft(note.body);
    setError(null);
    setEditing(true);
  };

  const saveEdit = async () => {
    if (!draft.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      await apiWrite(`/notes/${note.id}`, 'PATCH', { body: draft });
      setEditing(false);
      // Refetch rather than patch local state, so what's on screen is
      // always exactly what the server has -- including edited_by_name,
      // which this component doesn't otherwise know how to compute.
      onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteNote = async () => {
    // Any processor can delete any note in a shared thread, so a
    // mis-click would remove a colleague's work -- confirm first.
    if (!window.confirm('Delete this note? This cannot be undone.')) return;
    setDeleting(true);
    setError(null);
    try {
      await apiWrite(`/notes/${note.id}`, 'DELETE');
      onChanged();
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  };

  if (editing) {
    return (
      <li className="space-y-2">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={2}
          className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-zinc-200 focus:border-gold outline-none resize-y"
        />
        {error && <p className="text-xs text-red-400">{error}</p>}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={saveEdit}
            disabled={saving || !draft.trim()}
            className="px-3 py-1 rounded-md border border-gold/40 text-gold text-xs font-medium hover:bg-gold/10 transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button
            type="button"
            onClick={() => setEditing(false)}
            disabled={saving}
            className="px-3 py-1 rounded-md border border-zinc-700 text-zinc-400 text-xs font-medium hover:bg-zinc-800 transition disabled:opacity-40"
          >
            Cancel
          </button>
        </div>
      </li>
    );
  }

  return (
    <li>
      <p className="text-sm text-zinc-300">{note.body}</p>
      <p className="text-xs text-zinc-500">
        {note.authorName} · {formatDateTime(note.createdAt)}
        {note.editedByName ? ` · edited by ${note.editedByName}` : ''}
      </p>
      {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
      {canWrite && (
        <div className="flex items-center gap-3 mt-1">
          <button type="button" onClick={startEdit} className="text-xs text-zinc-500 hover:text-gold transition">
            Edit
          </button>
          <button
            type="button"
            onClick={deleteNote}
            disabled={deleting}
            className="text-xs text-zinc-500 hover:text-red-400 transition disabled:opacity-40"
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      )}
    </li>
  );
}

// Shared loan notes thread -- fetched only once this panel exists, i.e.
// once the row is expanded (LoanTable only renders LoanDetailPanel at
// all when isExpanded), so mounting this component IS "the panel is
// expanded" and a fetch on mount is exactly right, with no separate
// trigger needed.
//
// Loading, error, and empty are kept visibly distinct on purpose: an
// empty thread renders EmptyBlock ("No notes yet."), while a failed
// fetch or a { success: false } response renders ErrorBlock with the
// backend's own message. Collapsing those two into one "nothing to show"
// state would defeat the reason the backend returns a real 500 instead
// of an empty list in the first place (see notes-worker.js) -- a
// processor would have no way to tell a genuinely quiet loan from a
// notes service that's actually down.
function LoanNotesSection({ loan }) {
  const ariveLoanId = loan && loan.ariveLoanId;
  const canWrite = NOTES_WRITABLE_ROLES.includes(appConfig.user.role);

  const [state, setState] = useState({ data: null, loading: true, error: null });
  const [refetchTick, setRefetchTick] = useState(0);
  const refetch = () => setRefetchTick((t) => t + 1);

  useEffect(() => {
    if (!ariveLoanId) {
      setState({ data: [], loading: false, error: null });
      return undefined;
    }

    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true, error: null }));

    apiFetch(`/notes/${encodeURIComponent(ariveLoanId)}`)
      .then((data) => {
        if (!cancelled) setState({ data, loading: false, error: null });
      })
      .catch((err) => {
        if (!cancelled) setState({ data: null, loading: false, error: err.message });
      });

    return () => {
      cancelled = true;
    };
  }, [ariveLoanId, refetchTick]);

  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [addError, setAddError] = useState(null);

  const addNote = async () => {
    if (!draft.trim() || submitting) return;
    setSubmitting(true);
    setAddError(null);
    try {
      await apiWrite('/notes', 'POST', { ariveLoanId, body: draft });
      setDraft('');
      refetch();
    } catch (err) {
      setAddError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">Notes</p>

      {state.loading ? (
        <LoadingBlock label="Loading notes..." />
      ) : state.error ? (
        <ErrorBlock message={`Notes could not be loaded: ${state.error}`} onRetry={refetch} />
      ) : state.data.length === 0 ? (
        <EmptyBlock message="No notes yet." />
      ) : (
        <ul className="space-y-3 mb-4">
          {state.data.map((note) => (
            <NoteRow key={note.id} note={note} canWrite={canWrite} onChanged={refetch} />
          ))}
        </ul>
      )}

      {canWrite && (
        <div className="space-y-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Add a note for this loan..."
            rows={2}
            className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-zinc-200 placeholder-zinc-600 focus:border-gold outline-none resize-y"
          />
          {addError && <p className="text-xs text-red-400">{addError}</p>}
          <button
            type="button"
            onClick={addNote}
            disabled={submitting || !draft.trim()}
            className="px-3 py-1.5 rounded-md border border-gold/40 text-gold text-xs font-medium hover:bg-gold/10 transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? 'Adding...' : 'Add Note'}
          </button>
        </div>
      )}
    </div>
  );
}

// Fixed display order for the processor Loan Board's tracker strip.
const TRACKER_ORDER = ['APPRAISAL', 'TITLE', 'HOI', 'CD', 'FUNDING_WIRE', 'LE'];

// Short visible labels for each tracker, shown inside the indicator so the
// strip is readable without hovering.
const TRACKER_ABBR = {
  APPRAISAL: 'APRSL',
  TITLE: 'TTL',
  HOI: 'HOI',
  CD: 'CD',
  FUNDING_WIRE: 'FW',
  LE: 'LE',
};

const TRACKER_STATE_STYLES = {
  not_started: 'bg-zinc-800 text-zinc-500 border-zinc-700',
  in_progress: 'bg-gold/10 text-gold border-gold/40',
  complete: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
};

// Status vocabulary confirmed from live Arive payloads. Unrecognised values
// are treated as in-progress deliberately, so a new Arive status shows as
// active rather than silently reading as not-started.
function classifyTrackerStatus(status) {
  const s = (status || '').toUpperCase();
  const notStarted = ['NOT_ORDERED', 'NOT_SENT', 'NOT_REQUESTED', 'NOT_COMPLETE'];
  const complete = ['RECEIVED', 'COMPLETE', 'SENT', 'SIGNED'];
  if (!s || notStarted.includes(s)) return 'not_started';
  if (complete.includes(s)) return 'complete';
  return 'in_progress';
}

// LE (Loan Estimate) has no tracker status in Arive, so its state is derived
// from the initial/most-recent LE sent and signed dates on the loan instead.
function classifyLeState(loan) {
  const signed = getLoanDateValue(loan, 'MOST_RECENT_LE_SIGNED') || getLoanDateValue(loan, 'INITIAL_LE_SIGNED');
  if (signed) return { state: 'complete', title: `LE: signed ${signed}` };
  const sent = getLoanDateValue(loan, 'MOST_RECENT_LE_SENT') || getLoanDateValue(loan, 'INITIAL_LE_SENT');
  if (sent) return { state: 'in_progress', title: `LE: sent ${sent}, not signed` };
  return { state: 'not_started', title: 'LE: not sent' };
}

function TrackerStrip({ loan }) {
  const list = (loan && loan.trackers) || [];
  return (
    <div className="flex items-center gap-1">
      {TRACKER_ORDER.map((name) => {
        let state;
        let titleText;
        if (name === 'LE') {
          const le = classifyLeState(loan);
          state = le.state;
          titleText = le.title;
        } else {
          const entry = list.find((t) => t.trackerName === name);
          const rawStatus = (entry && entry.status) || '';
          state = classifyTrackerStatus(rawStatus);
          titleText = rawStatus ? `${name}: ${rawStatus}` : `${name}: not started`;
        }
        return (
          <span
            key={name}
            title={titleText}
            className={`inline-flex items-center justify-center h-5 w-6 rounded text-[9px] font-semibold border leading-none ${TRACKER_STATE_STYLES[state]}`}
          >
            {TRACKER_ABBR[name]}
          </span>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Briefing Room
// ---------------------------------------------------------------------------

// Roles that can be a loan's officer -- the Active Loans card below is
// scoped to loans where appConfig.user.email matches loanOfficerEmail
// (see activeLoans), so a pure processor originates nothing and the card
// would always read "No active loans." Hiding it for that role is
// correct rather than cosmetic: an always-empty card just trains people
// to stop looking at that part of the screen. Named list + .includes(),
// matching NOTES_WRITABLE_ROLES' pattern, rather than an inline role
// comparison.
const LOAN_ORIGINATION_ROLES = ['admin', 'ceo', 'loan_officer', 'loan_officer_processor'];

function BriefingRoomTab() {
  const emails = useApiData('/emails');
  const events = useApiData('/calendar');
  const todos = useApiData('/todos/grouped');
  const flagged = useApiData('/flagged-emails');
  const loans = useApiData('/loans');
  const briefing = useApiData('/briefing');

  if (emails.loading || events.loading || todos.loading || flagged.loading || loans.loading || briefing.loading) {
    return <LoadingBlock label="Building today's briefing..." />;
  }
  if (emails.error && events.error && todos.error && flagged.error && loans.error) {
    return (
      <ErrorBlock
        message={emails.error}
        onRetry={() => {
          emails.retry();
          events.retry();
          todos.retry();
          flagged.retry();
          loans.retry();
        }}
      />
    );
  }

  const today = new Date();
  const emailList = emails.data || [];
  const eventList = events.error ? [] : events.data || [];
  const flaggedList = flagged.data || [];
  const loanList = loans.data || [];

  const todaysEmails = emailList.filter((e) => e.receivedAt && isSameDay(new Date(e.receivedAt), today));
  const ai = (briefing.data && briefing.data.aiBriefing) || null;
  const todaysEvents = eventList
    .filter((ev) => ev.startTime && isEventOnDay(ev, today))
    .sort((a, b) => eventCompareDate(a) - eventCompareDate(b));

  const categoryCounts = emailList.reduce((acc, e) => {
    const c = e.category || 'uncategorized';
    acc[c] = (acc[c] || 0) + 1;
    return acc;
  }, {});

  // Grouped todos flattened back into one array — group/list structure
  // doesn't matter for the briefing, only due dates and priority.
  const allTasks = (todos.data || []).flatMap((g) => g.lists.flatMap((l) => l.tasks));

  // "This week" reuses getWeekDays' Sun-Sat convention (same window
  // CalendarTab shows), trimmed to start the day AFTER today since today's
  // items already appear in Today's Attention above.
  const weekDays = getWeekDays(today);
  const endOfWeek = new Date(weekDays[6]);
  endOfWeek.setHours(23, 59, 59, 999);
  const startOfTomorrow = new Date(today);
  startOfTomorrow.setHours(0, 0, 0, 0);
  startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);

  const combinedDependencyErrored = todos.error && events.error;

  const todaysTaskItems = todos.error
    ? []
    : allTasks
        .filter((t) => t.dueDate && isSameDay(new Date(t.dueDate), today))
        .map((t) => ({
          type: 'task',
          key: `task-${t.id}`,
          label: t.task,
          timeLabel: 'Due today',
          priority: t.priority,
          sortTime: new Date(t.dueDate),
        }));
  const todaysEventItems = todaysEvents.map((ev) => ({
    type: 'event',
    key: `event-${ev.id}`,
    label: ev.title,
    timeLabel: formatEventTime(ev),
    sortTime: eventCompareDate(ev),
  }));
  const todaysAttention = [...todaysTaskItems, ...todaysEventItems].sort((a, b) => a.sortTime - b.sortTime);

  const thisWeekTaskItems = todos.error
    ? []
    : allTasks
        .filter((t) => {
          if (!t.dueDate) return false;
          const d = new Date(t.dueDate);
          return d >= startOfTomorrow && d <= endOfWeek;
        })
        .map((t) => ({
          type: 'task',
          key: `task-${t.id}`,
          label: t.task,
          timeLabel: `Due ${formatDate(t.dueDate)}`,
          priority: t.priority,
          sortTime: new Date(t.dueDate),
        }));
  const thisWeekEventItems = eventList
    .filter((ev) => {
      if (!ev.startTime) return false;
      const d = eventCompareDate(ev);
      return d && d >= startOfTomorrow && d <= endOfWeek;
    })
    .map((ev) => ({
      type: 'event',
      key: `event-${ev.id}`,
      label: ev.title,
      timeLabel: formatEventDateTime(ev),
      sortTime: eventCompareDate(ev),
    }));
  const thisWeekAttention = [...thisWeekTaskItems, ...thisWeekEventItems].sort((a, b) => a.sortTime - b.sortTime);

  const currentUserEmail = (appConfig.user.email || '').trim().toLowerCase();
  const activeLoans = loanList
    .filter((l) => isActiveLoanStatus(l.status))
    .filter((l) => !currentUserEmail || (l.loanOfficerEmail || '').trim().toLowerCase() === currentUserEmail);

  const attentionRow = (item) => (
    <li key={item.key} className="py-3 flex items-center gap-3">
      <span
        className={`shrink-0 px-2 py-0.5 rounded-full text-[11px] font-medium border ${
          item.type === 'task'
            ? 'bg-sky-500/10 text-sky-400 border-sky-500/30'
            : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
        }`}
      >
        {item.type === 'task' ? 'Task' : 'Event'}
      </span>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-zinc-100 truncate">{item.label}</p>
        <p className="text-xs text-zinc-500">{item.timeLabel}</p>
      </div>
      {item.priority && (
        <span
          className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-medium border capitalize whitespace-nowrap ${
            PRIORITY_STYLES[item.priority] || PRIORITY_STYLES.normal
          }`}
        >
          {item.priority}
        </span>
      )}
    </li>
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Emails Today" value={todaysEmails.length} />
        <StatCard label="Priority Emails" value={(ai && ai.urgent && ai.urgent.length) || 0} />
        <StatCard label="Events Today" value={todaysEvents.length} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-5">
            <SectionHeading>Email Briefing</SectionHeading>
            {!ai || ((ai.urgent || []).length === 0 && (ai.actionRequired || []).length === 0 && (ai.activeClients || []).length === 0 && (ai.fyi || []).length === 0) ? (
              <EmptyBlock message="No briefing yet — it generates on the next sync." />
            ) : (
              (() => {
                const aiItemRow = (item, idx) => {
                  const rowContent = (
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-zinc-100 truncate">{item.subject}</p>
                      <p className="text-xs text-zinc-500 truncate">{item.from}</p>
                      {item.summary && <p className="text-xs text-zinc-400 line-clamp-2 mt-0.5">{item.summary}</p>}
                    </div>
                  );

                  return (
                    <li key={`${item.webLink || item.subject}-${idx}`} className="py-3">
                      {item.webLink ? (
                        <a
                          href={item.webLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 hover:text-gold transition"
                        >
                          {rowContent}
                        </a>
                      ) : (
                        <div className="flex items-center gap-3">{rowContent}</div>
                      )}
                    </li>
                  );
                };

                const aiSection = (label, colorClass, items) => {
                  if (!items || items.length === 0) return null;
                  return (
                    <div key={label} className="mb-4 last:mb-0">
                      <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${colorClass}`}>{label}</p>
                      <ul className="divide-y divide-zinc-800">{items.map((item, idx) => aiItemRow(item, idx))}</ul>
                    </div>
                  );
                };

                return (
                  <>
                    {aiSection('Urgent', 'text-red-400', ai.urgent)}
                    {aiSection('Action Required', 'text-gold', ai.actionRequired)}
                    {aiSection('Active Clients', 'text-sky-400', ai.activeClients)}
                    {aiSection('FYI', 'text-zinc-400', ai.fyi)}
                    <p className="text-xs text-zinc-500 pt-3 border-t border-zinc-800 mt-1 text-center">
                      Reviewed {ai.totalCount} emails
                    </p>
                  </>
                );
              })()
            )}
          </Card>

          <Card className="p-5">
            <SectionHeading>Email Summary by Category</SectionHeading>
            {emails.error ? (
              <ErrorBlock message={emails.error} onRetry={emails.retry} />
            ) : Object.keys(categoryCounts).length === 0 ? (
              <EmptyBlock message="No emails yet." />
            ) : (
              <div className="flex flex-wrap gap-3">
                {Object.entries(categoryCounts).map(([cat, count]) => (
                  <div key={cat} className="px-4 py-2 rounded-lg bg-zinc-900 border border-zinc-800 text-sm capitalize">
                    <span className="text-zinc-400">{cat}</span>
                    <span className="text-gold font-semibold ml-2">{count}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="p-5">
            <SectionHeading>Today's Schedule</SectionHeading>
            {events.error ? (
              <ErrorBlock message={events.error} onRetry={events.retry} />
            ) : todaysEvents.length === 0 ? (
              <EmptyBlock message="Nothing scheduled today." />
            ) : (
              <ul className="divide-y divide-zinc-800">
                {todaysEvents.map((ev) => {
                  const rowContent = (
                    <>
                      <span className="text-gold text-sm font-mono w-20 shrink-0">{formatEventTime(ev)}</span>
                      <div className="min-w-0">
                        <p className="font-medium break-words">{ev.title}</p>
                        {ev.location && <p className="text-xs text-zinc-500 truncate">{ev.location}</p>}
                      </div>
                    </>
                  );
                  return (
                    <li key={ev.id} className="py-3 flex items-center gap-3">
                      {ev.webLink ? (
                        <a
                          href={ev.webLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="contents hover:text-gold transition"
                        >
                          {rowContent}
                        </a>
                      ) : (
                        rowContent
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>

          {LOAN_ORIGINATION_ROLES.includes(appConfig.user.role) && (
            <Card className="p-5">
              <SectionHeading>Active Loans</SectionHeading>
              {loans.error ? (
                <ErrorBlock message={loans.error} onRetry={loans.retry} />
              ) : activeLoans.length === 0 ? (
                <EmptyBlock message="No active loans." />
              ) : (
                <ul className="divide-y divide-zinc-800">
                  {activeLoans.slice(0, 8).map((l) => (
                    <li key={l.id} className="py-3 flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium text-zinc-100 truncate">{l.borrowerName || '—'}</p>
                        <p className="text-xs text-zinc-500 truncate">{l.stage || '—'}</p>
                      </div>
                      <StatusBadge status={l.status} />
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}

          <Card className="p-5">
            <SectionHeading>Today's Attention</SectionHeading>
            {combinedDependencyErrored ? (
              <ErrorBlock
                message={todos.error || events.error}
                onRetry={() => {
                  todos.retry();
                  events.retry();
                }}
              />
            ) : todaysAttention.length === 0 ? (
              <EmptyBlock message="Nothing urgent today." />
            ) : (
              <ul className="divide-y divide-zinc-800">{todaysAttention.map(attentionRow)}</ul>
            )}
          </Card>

          <Card className="p-5">
            <SectionHeading>This Week's Attention</SectionHeading>
            {combinedDependencyErrored ? (
              <ErrorBlock
                message={todos.error || events.error}
                onRetry={() => {
                  todos.retry();
                  events.retry();
                }}
              />
            ) : thisWeekAttention.length === 0 ? (
              <EmptyBlock message="Nothing else on deck this week." />
            ) : (
              <ul className="divide-y divide-zinc-800">{thisWeekAttention.map(attentionRow)}</ul>
            )}
          </Card>

          <Card className="p-5">
            <SectionHeading>Flagged Emails</SectionHeading>
            {flagged.error ? (
              <ErrorBlock message={flagged.error} onRetry={flagged.retry} />
            ) : flaggedList.length === 0 ? (
              <EmptyBlock message="No flagged emails." />
            ) : (
              <ul className="divide-y divide-zinc-800">
                {flaggedList.slice(0, 8).map((e, idx) => (
                  <li key={e.webLink || `${e.subject}-${idx}`} className="py-3">
                    <a
                      href={e.webLink || undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block hover:text-gold transition"
                    >
                      <p className="font-medium text-zinc-100 truncate">{e.subject}</p>
                      <p className="text-xs text-zinc-500 truncate">{e.from}</p>
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Calendar
// ---------------------------------------------------------------------------

function CalendarTab() {
  const events = useApiData('/calendar');
  const [weekOffset, setWeekOffset] = useState(0);

  if (events.loading) return <LoadingBlock label="Loading calendar..." />;
  if (events.error) return <ErrorBlock message={events.error} onRetry={events.retry} />;

  const anchor = new Date();
  anchor.setDate(anchor.getDate() + weekOffset * 7);
  const days = getWeekDays(anchor);
  const list = events.data || [];

  const navBtn = 'px-3 py-1.5 rounded-md border border-zinc-700 text-xs sm:text-sm text-zinc-300 hover:border-gold hover:text-gold transition';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SectionHeading>
          Week of {formatDate(days[0])} – {formatDate(days[6])}
        </SectionHeading>
        <div className="flex gap-2">
          <button className={navBtn} onClick={() => setWeekOffset((w) => w - 1)}>
            ‹ Prev
          </button>
          <button className={navBtn} onClick={() => setWeekOffset(0)}>
            Today
          </button>
          <button className={navBtn} onClick={() => setWeekOffset((w) => w + 1)}>
            Next ›
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3">
        {days.map((day) => {
          const dayEvents = list
            .filter((ev) => ev.startTime && isEventOnDay(ev, day))
            .sort((a, b) => eventCompareDate(a) - eventCompareDate(b));
          const today = isSameDay(day, new Date());
          return (
            <Card key={day.toISOString()} className={`p-3 min-h-[160px] ${today ? 'border-gold/60' : ''}`}>
              <p className={`text-xs font-semibold uppercase tracking-wide ${today ? 'text-gold' : 'text-zinc-500'}`}>
                {day.toLocaleDateString(undefined, { weekday: 'short' })}
              </p>
              <p className="text-lg font-bold mb-2 text-zinc-100">{day.getDate()}</p>
              {dayEvents.length === 0 ? (
                <p className="text-xs text-zinc-600">No events</p>
              ) : (
                <ul className="space-y-2">
                  {dayEvents.map((ev) => {
                    const rowContent = (
                      <>
                        <p className="text-gold font-mono">{formatEventTime(ev)}</p>
                        <p className="text-zinc-200 font-medium break-words">{ev.title}</p>
                        {ev.location && <p className="text-zinc-500 break-words">{ev.location}</p>}
                      </>
                    );
                    return (
                      <li key={ev.id} className="text-xs bg-zinc-900 border border-zinc-800 rounded-md p-2">
                        {ev.webLink ? (
                          <a
                            href={ev.webLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="contents hover:text-gold transition"
                          >
                            {rowContent}
                          </a>
                        ) : (
                          rowContent
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Active Loans
// ---------------------------------------------------------------------------

function isActiveLoanStatus(status) {
  const s = (status || '').toUpperCase();
  if (!s) return false; // no status -> don't show
  // Loans now show on Active Loans from application intake onward, so loan
  // officers can see and follow up on early-stage files. Previously
  // everything before Disclosed was hidden here.
  //
  // Arive shows Adverse as a terminal state in its UI, alongside Suspended,
  // with a separate "adverse reason" field. Loans become read-only once
  // adversed.
  //
  // ADVERSE is now CONFIRMED by Arive support's authoritative enum list,
  // and by a live payload: Two loans (17227274, 17280231) were set to the
  // literal status ADVERSE by hand in Keith's database, because their
  // adverse transition never reached the dashboard. SUSPENDED is confirmed
  // the same way, alongside it, as the other terminal state Arive groups
  // under its "Others" row.
  //
  // This function deliberately differs from isProcessorLoanStatus: Active
  // Loans starts at application intake so officers can chase early files,
  // while the Loan Board starts later, at Loan Setup, where processing
  // begins. Do not merge them.
  // Hidden: funded stages (these belong in Pipeline, not Active)
  const funded = ['FUNDED', 'COMMISSION_PAID', 'BROKER_CHECK'];
  // Hidden: terminal loans (adverse or suspended, read-only in Arive)
  const terminal = ['ADVERSE', 'SUSPENDED'];
  if (funded.some((f) => s.includes(f))) return false;
  if (terminal.some((t) => s.includes(t))) return false;
  return true; // Intake through Closing -> show
}

// A processor's work starts when a loan enters processing at Loan Setup.
// Earlier statuses belong only on a loan officer's Active Loans tab.
//
// The Loan Board and the Pipeline tab share this floor. PIPELINE_EXCLUDED_
// STATUSES expresses the same rule for Pipeline using exact matching
// rather than substring matching. If the set of pre-processing statuses
// changes, BOTH need updating.
//
// Adverse and suspended loans are excluded here for the same reason they
// are excluded from Active Loans and Pipeline -- both are now confirmed
// terminal states, per Arive support's authoritative enum list (see the
// note in isActiveLoanStatus).
function isProcessorLoanStatus(status) {
  const s = (status || '').toUpperCase();
  if (!s) return false; // no status -> don't show
  // Hidden: pre-processing stages (belong only on Active Loans)
  const preProcessing = ['PREAPPROVED', 'APPLICATION_INTAKE', 'PIPELINE'];
  // Hidden: funded stages (these belong in Pipeline, not the Loan Board)
  const funded = ['FUNDED', 'COMMISSION_PAID', 'BROKER_CHECK'];
  // Hidden: terminal loans (adverse or suspended, read-only in Arive)
  const terminal = ['ADVERSE', 'SUSPENDED'];
  if (preProcessing.some((p) => s.includes(p))) return false;
  if (funded.some((f) => s.includes(f))) return false;
  if (terminal.some((t) => s.includes(t))) return false;
  return true; // Loan Setup through Closing -> show
}

function ActiveLoansTab() {
  const loans = useApiData('/loans-with-trackers');
  if (loans.loading) return <LoadingBlock label="Loading active loans..." />;
  if (loans.error) return <ErrorBlock message={loans.error} onRetry={loans.retry} />;

  // Ownership used to be enforced entirely at ingest, via LOAN_OWNER_EMAIL
  // scoping the Arive webhook so a tenant's database only ever held that
  // user's own loans (see upsertLoanFromArive in backend/handlers.js).
  // Tenants that need a company-wide Pipeline no longer have that ingest
  // filter, so their database can hold every loan officer's loans -- Active
  // Loans has to scope itself client-side now instead of inheriting
  // ownership for free from what's in the database. This is a no-op on
  // tenants whose database still only holds their own loans.
  const currentUserEmail = (appConfig.user.email || '').trim().toLowerCase();
  const active = (loans.data || [])
    .filter((l) => isActiveLoanStatus(l.status))
    .filter((l) => !currentUserEmail || (l.loanOfficerEmail || '').trim().toLowerCase() === currentUserEmail);

  // loanAmount may arrive as a number, a numeric string, null, undefined,
  // or 0 -- anything that isn't a positive finite number counts as "no
  // amount" and is excluded from the sum, just tallied as pending.
  const amountStats = active.reduce(
    (acc, l) => {
      const n = Number(l.loanAmount);
      if (Number.isFinite(n) && n > 0) {
        acc.total += n;
      } else {
        acc.pendingCount += 1;
      }
      return acc;
    },
    { total: 0, pendingCount: 0 }
  );
  const headingParts = [`Active Loans (${active.length})`];
  if (amountStats.total > 0) headingParts.push(formatCurrency(amountStats.total));
  if (amountStats.pendingCount > 0) headingParts.push(`${amountStats.pendingCount} pending amount`);

  return (
    <div className="space-y-4">
      <SectionHeading>{headingParts.join(' · ')}</SectionHeading>
      {/* showTeam is deliberately omitted -- every row here already belongs
          to the logged-in officer, so the Loan Officer/Processor columns
          would be redundant. showTrackers still surfaces the tracker
          strip, closing date, and lock expiry. */}
      <LoanTable loans={active} showTrackers />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pipeline (company-wide view — every loan grouped by where it is in the
// process, not by loan officer, and not filtered to funded loans)
// ---------------------------------------------------------------------------

// Arive has a two-level model: a Pipeline tier (Processing, Closing,
// Funded) and a granular Loan Status inside it. The webhook only sends the
// granular status, so this grouping is derived here rather than trusted
// from upstream. Apex's own operational pipeline, ordered most-advanced
// first:
//   - Confirmed in live Arive webhook payloads: APPROVED_WITH_CONDITION,
//     UNDERWRITING_SUBMITTED, DOCS_OUT, DOCS_SIGNED, LOAN_FUNDED,
//     BROKER_CHECK_RECEIVED. LOAN_FUNDED and BROKER_CHECK_RECEIVED were
//     originally guessed as FUNDED and BROKER_CHECK (inherited from
//     isActiveLoanStatus); those guesses missed and the loans landed in
//     Other with their raw status visible, which is how the real enum
//     values were found.
//   - CLEAR_TO_CLOSE, LOAN_SETUP and RE_SUBMITTAL were originally guessed
//     from Arive's UI display names, following the APPROVED_WITH_CONDITION
//     naming pattern -- all three are now CONFIRMED in live data, observed
//     directly in tenant databases.
//   - DISCLOSURE_SENT is newly confirmed the same way and newly mapped
//     here, alongside LOAN_SETUP: once a loan is disclosed it moves into
//     processing, so it belongs in the same stage.
//   - RESUBMITTAL (no underscore) remains an unconfirmed alternate
//     spelling of RE_SUBMITTAL, kept as a safety net.
//   - Arive support has since provided the complete authoritative enum
//     list. FINALIZED never existed as a real value -- it was a guess
//     inherited from an earlier version of this code -- and has been
//     corrected everywhere to the real value, COMMISSION_PAID (Arive's UI
//     label is "Loan Finalized"). SUSPENDED is confirmed as a separate
//     terminal state, grouped by Arive under its "Others" row alongside
//     Adverse, and is now excluded via PIPELINE_EXCLUDED_STATUSES rather
//     than mapped to a stage here.
// A wrong guess is self-correcting: the loan lands in the Other stage below
// with its raw status visible, which is how we learn what enum value to
// add next -- the same mechanism that found DISCLOSURE_SENT.
const PIPELINE_STAGES = [
  { id: 'CLOSED_FUNDED', label: 'Closed/Funded', statuses: ['LOAN_FUNDED', 'COMMISSION_PAID', 'BROKER_CHECK_RECEIVED'] },
  { id: 'CLEAR_TO_CLOSE', label: 'Clear to Close', statuses: ['CLEAR_TO_CLOSE', 'DOCS_OUT', 'DOCS_SIGNED'] },
  { id: 'FINAL_REVIEW', label: 'Final Review', statuses: ['RE_SUBMITTAL', 'RESUBMITTAL'] },
  { id: 'CONDITIONALLY_APPROVED', label: 'Conditionally Approved', statuses: ['APPROVED_WITH_CONDITION'] },
  { id: 'IN_UNDERWRITING', label: 'In Underwriting', statuses: ['UNDERWRITING_SUBMITTED'] },
  { id: 'SUBMITTED_TO_PROCESSING', label: 'Submitted to Processing', statuses: ['LOAN_SETUP', 'DISCLOSURE_SENT'] },
];

// Pre-processing statuses that belong only on a loan officer's Active
// Loans tab, never on the company-wide Pipeline. The Other stage in
// PipelineTab is deliberately reserved for genuinely unrecognised statuses
// so it stays a meaningful signal about what Arive is sending that we
// haven't accounted for yet -- filtering these out by an explicit list
// (rather than just leaving them unmapped, which would dump them into
// Other) is what keeps that signal clean. 'PIPELINE' covers loans created
// without a status, which default to the literal string 'pipeline' in the
// loans table.
// 'ADVERSE' and 'SUSPENDED' loans are both terminal states and belong on no
// dashboard surface -- Arive remains the system of record for them.
const PIPELINE_EXCLUDED_STATUSES = ['PREAPPROVED', 'APPLICATION_INTAKE', 'PIPELINE', 'ADVERSE', 'SUSPENDED'];

// Loans created without a status default to the string 'pipeline' in the
// loans table, so that value -- along with any status Arive sends that
// isn't in PIPELINE_STAGES yet -- will legitimately land in OTHER.
function resolveLoanStage(status) {
  const s = (status || '').toUpperCase();
  if (!s) return 'OTHER';
  const stage = PIPELINE_STAGES.find((st) => st.statuses.includes(s));
  return stage ? stage.id : 'OTHER';
}

// Same match as resolveLoanStage above (uppercased/trimmed, first stage
// whose statuses array includes it), but returns the stage object itself
// rather than just its id -- used by LoanTable's Milestone cell
// (resolveMilestoneLabel), which needs the stage's label, not its id.
// Kept separate from resolveLoanStage rather than changing what that
// function returns, since resolveLoanStage's callers only ever need the
// id.
function findPipelineStage(status) {
  const s = (status || '').toUpperCase().trim();
  if (!s) return null;
  return PIPELINE_STAGES.find((st) => st.statuses.includes(s)) || null;
}

// Same Card + SectionHeading + toggle-button structure as LoanOfficerSection,
// just keyed by pipeline stage instead of loan officer. showRawStatuses is
// only ever true for the Other stage, where the raw status values that
// didn't match PIPELINE_STAGES need to be visible so we know what to add.
function PipelineStageSection({
  label,
  loans,
  showRawStatuses = false,
  showMonthMove = false,
  monthOptions = [],
  onMonthMoved = () => {},
}) {
  const [expanded, setExpanded] = useState(true);
  const totalAmount = loans.reduce((sum, l) => sum + (Number(l.loanAmount) || 0), 0);

  const rawStatusCounts = showRawStatuses
    ? loans.reduce((acc, l) => {
        const raw = l.status || '(no status)';
        acc[raw] = (acc[raw] || 0) + 1;
        return acc;
      }, {})
    : null;

  return (
    <Card className="p-5">
      <SectionHeading
        right={
          <span className="text-sm text-zinc-500">
            {loans.length} loan{loans.length === 1 ? '' : 's'} • {formatCurrency(totalAmount)}
          </span>
        }
      >
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
          aria-label={`Toggle loans for ${label}`}
          className="text-left uppercase"
        >
          {expanded ? '▾' : '▸'} {label}
        </button>
      </SectionHeading>
      {expanded && (
        <>
          {rawStatusCounts && (
            <div className="flex flex-wrap gap-2 mb-3">
              {Object.entries(rawStatusCounts).map(([raw, count]) => (
                <span
                  key={raw}
                  className="px-2 py-0.5 rounded-full text-[11px] font-medium border bg-zinc-500/10 text-zinc-300 border-zinc-500/30"
                >
                  {raw} ({count})
                </span>
              ))}
            </div>
          )}
          <LoanTable
            loans={loans}
            showTrackers
            showTeam
            showMonthMove={showMonthMove}
            monthOptions={monthOptions}
            onMonthMoved={onMonthMoved}
          />
        </>
      )}
    </Card>
  );
}

// Built without a date library, on purpose -- a month key is a plain
// "YYYY-MM" string throughout this section (same convention the backend's
// loan_month_overrides table uses), so every operation on it is string
// math, never a Date object standing in for a month.
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function monthKeyFromDate(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function formatMonthLabel(monthKey) {
  const [year, month] = monthKey.split('-').map(Number);
  return `${MONTH_NAMES[month - 1]} ${year}`;
}

function nextMonthKey(monthKey) {
  const [year, month] = monthKey.split('-').map(Number);
  return month === 12 ? `${year + 1}-01` : `${year}-${String(month + 1).padStart(2, '0')}`;
}

// The contiguous sequence of "YYYY-MM" keys from startKey through endKey
// inclusive, with no gaps -- a month between two populated months is
// included even when empty, so month buttons never skip. "YYYY-MM" keys
// compare correctly with plain string <= (same trick this repo already
// uses for ISO timestamps elsewhere, e.g. router-worker.js's prune
// cutoffs), so the loop condition needs no date arithmetic beyond
// stepping to the next month. Capped well beyond any real pipeline span
// so one out-of-range override month (bad data, not normal use) can't
// hang the tab building an enormous range.
function buildMonthRange(startKey, endKey) {
  const months = [];
  let cursor = startKey;
  let guard = 0;
  while (cursor <= endKey && guard < 240) {
    months.push(cursor);
    cursor = nextMonthKey(cursor);
    guard += 1;
  }
  return months;
}

// A saved override wins over the closing date -- see notes-schema.sql's
// comment on loan_month_overrides for why. Falls back to
// CLOSING_CONTINGENCY (see LoanTable's CLOSING column and
// makeClosingDatePassedRule for why that field, not EARLIEST_CLOSING, is
// what this app treats as "the" closing date) when there's no override.
//
// EARLIEST_CLOSING is a last resort, used only when CLOSING_CONTINGENCY is
// entirely absent -- in practice a closed loan whose contingency date was
// never populated. This is NOT a reversal of the decision to prefer
// CLOSING_CONTINGENCY: the Loan Board column, the stale-file alert, and
// the primary lookup just above still read CLOSING_CONTINGENCY only, and
// nowhere else in the app treats EARLIEST_CLOSING as a working date. It
// exists purely to fill a gap in the data for this one function, where
// every loan needs *some* month rather than none.
//
// If neither date exists, this falls back to the current month rather
// than returning null. A loan with no date anywhere still needs to land
// somewhere a processor will actually see it -- LoanTable's CLOSING
// column will render empty for that loan regardless of which month it's
// grouped under, and that empty cell, not a separate section, is the
// prompt to go set a real date in Arive. This function never returns
// null.
function monthKeyForLoan(loan, overridesByLoanId) {
  const override = overridesByLoanId[loan.ariveLoanId];
  if (override && override.month) return override.month;

  const closingContingencyDate = parseLoanDate(getLoanDateValue(loan, 'CLOSING_CONTINGENCY'));
  if (closingContingencyDate) return monthKeyFromDate(closingContingencyDate);

  const earliestClosingDate = parseLoanDate(getLoanDateValue(loan, 'EARLIEST_CLOSING'));
  if (earliestClosingDate) return monthKeyFromDate(earliestClosingDate);

  return monthKeyFromDate(new Date());
}

function PipelineTab() {
  const loans = useApiData('/loans-with-trackers');
  const overrides = useApiData('/month-overrides');
  const [selectedMonth, setSelectedMonth] = useState(null);

  if (loans.loading || overrides.loading) return <LoadingBlock label="Loading pipeline..." />;
  if (loans.error) return <ErrorBlock message={loans.error} onRetry={loans.retry} />;

  // Pre-processing loans (see PIPELINE_EXCLUDED_STATUSES) never belong on
  // this tab -- filtered out before grouping so they can't land in a stage
  // or in Other. Comparison is uppercased/trimmed to match how
  // resolveLoanStage normalises status below.
  const all = (loans.data || []).filter(
    (l) => !PIPELINE_EXCLUDED_STATUSES.includes((l.status || '').toUpperCase().trim())
  );

  if (all.length === 0) {
    return <EmptyBlock message="No loans in the pipeline yet. This isn't an error -- there's simply nothing to show." />;
  }

  // The overrides fetch failing must never fail the whole tab, and must
  // never be treated as "no overrides exist" -- either would put a loan
  // in the wrong month with no explanation. Falls back to grouping by
  // closing date alone, with an explicit notice below rather than silent
  // substitution.
  const overridesFailed = Boolean(overrides.error);
  const overridesByLoanId = overridesFailed ? {} : overrides.data || {};

  const totalAmount = all.reduce((sum, l) => sum + (Number(l.loanAmount) || 0), 0);

  // monthKeyForLoan never returns null (see its own comment) -- every
  // loan lands in loansByMonth, none are set aside separately.
  const loansByMonth = new Map();
  for (const loan of all) {
    const monthKey = monthKeyForLoan(loan, overridesByLoanId);
    // Carries the loan's resolved current month (and whether it came from
    // a saved override) onto the loan object itself, so LoanMonthMoveControl
    // can read it directly off each row without LoanTable needing its own
    // copy of the override map -- just the month-range and refetch props
    // it already threads through.
    const enrichedLoan = {
      ...loan,
      pipelineMonthKey: monthKey,
      pipelineMonthOverride: overridesByLoanId[loan.ariveLoanId] || null,
    };
    if (!loansByMonth.has(monthKey)) loansByMonth.set(monthKey, []);
    loansByMonth.get(monthKey).push(enrichedLoan);
  }

  const currentMonthKey = monthKeyFromDate(new Date());
  const populatedMonthKeys = Array.from(loansByMonth.keys());
  const allRangeKeys = [...populatedMonthKeys, currentMonthKey].sort();
  const monthRange = buildMonthRange(allRangeKeys[0], allRangeKeys[allRangeKeys.length - 1]);

  const defaultMonth = monthRange.includes(currentMonthKey) ? currentMonthKey : monthRange[0];
  const effectiveSelectedMonth =
    selectedMonth && monthRange.includes(selectedMonth) ? selectedMonth : defaultMonth;
  const selectedMonthLoans = loansByMonth.get(effectiveSelectedMonth) || [];

  const groups = new Map();
  for (const loan of selectedMonthLoans) {
    const stageId = resolveLoanStage(loan.status);
    if (!groups.has(stageId)) groups.set(stageId, []);
    groups.get(stageId).push(loan);
  }

  const orderedStages = [...PIPELINE_STAGES, { id: 'OTHER', label: 'Other' }];

  return (
    <div className="space-y-4">
      <SectionHeading
        right={
          <span className="text-sm text-zinc-500">
            {all.length} loan{all.length === 1 ? '' : 's'} • {formatCurrency(totalAmount)}
          </span>
        }
      >
        Pipeline
      </SectionHeading>

      {overridesFailed && (
        <div className="px-4 py-3 rounded-lg border border-red-900/40 bg-red-950/10 text-sm text-red-300">
          Saved month assignments could not be loaded ({overrides.error}). Loans below are grouped by closing
          date only, so a loan someone moved to a different month may show under its original month right now.
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {monthRange.map((monthKey) => {
          const monthLoans = loansByMonth.get(monthKey) || [];
          const monthTotal = monthLoans.reduce((sum, l) => sum + (Number(l.loanAmount) || 0), 0);
          const isSelected = monthKey === effectiveSelectedMonth;
          return (
            <button
              key={monthKey}
              type="button"
              onClick={() => setSelectedMonth(monthKey)}
              aria-pressed={isSelected}
              className={`px-4 py-2 rounded-lg border text-left transition ${
                isSelected ? 'bg-gold/10 border-gold text-gold' : 'bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-zinc-700'
              }`}
            >
              <p className="text-sm font-semibold">{formatMonthLabel(monthKey)}</p>
              <p className={`text-xs ${isSelected ? 'text-gold/80' : 'text-zinc-500'}`}>
                {monthLoans.length} loan{monthLoans.length === 1 ? '' : 's'} • {formatCurrency(monthTotal)}
              </p>
            </button>
          );
        })}
      </div>

      {orderedStages.map((stage) => {
        const stageLoans = groups.get(stage.id);
        if (!stageLoans || stageLoans.length === 0) return null;
        return (
          <PipelineStageSection
            key={stage.id}
            label={stage.label}
            loans={stageLoans}
            showRawStatuses={stage.id === 'OTHER'}
            showMonthMove
            monthOptions={monthRange}
            onMonthMoved={overrides.retry}
          />
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loan Board (processor role — all active loans grouped by loan officer)
// ---------------------------------------------------------------------------
//
// MINIMAL first version: proves the /loans-with-trackers endpoint, the
// officer grouping, and the tab wiring. Deliberately does not render the
// trackers/dates arrays the endpoint already returns -- the tracker status
// grid comes later, once real Arive webhook data exists to design against.

function LoanOfficerSection({ officerName, loans }) {
  const [expanded, setExpanded] = useState(true);
  const totalAmount = loans.reduce((sum, l) => sum + (Number(l.loanAmount) || 0), 0);

  return (
    <Card className="p-5">
      <SectionHeading
        right={
          <span className="text-sm text-zinc-500">
            {loans.length} loan{loans.length === 1 ? '' : 's'} • {formatCurrency(totalAmount)}
          </span>
        }
      >
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
          aria-label={`Toggle loans for ${officerName}`}
          className="text-left uppercase"
        >
          {expanded ? '▾' : '▸'} {officerName}
        </button>
      </SectionHeading>
      {expanded && <LoanTable loans={loans} showTrackers showTeam />}
    </Card>
  );
}

function LoanBoardTab() {
  const loans = useApiData('/loans-with-trackers');
  if (loans.loading) return <LoadingBlock label="Loading loan board..." />;
  if (loans.error) return <ErrorBlock message={loans.error} onRetry={loans.retry} />;

  const active = (loans.data || []).filter((l) => isProcessorLoanStatus(l.status));

  if (active.length === 0) {
    return <EmptyBlock message="No active loans." />;
  }

  const groups = new Map();
  for (const loan of active) {
    const officerName = loan.loanOfficerName || loan.loanOfficerEmail || 'Unassigned';
    if (!groups.has(officerName)) groups.set(officerName, []);
    groups.get(officerName).push(loan);
  }

  // 'Unassigned' always sorts last regardless of alphabetical order, same
  // convention as listTodosGrouped's 'Ungrouped' bucket on the backend.
  const sortedOfficerNames = [...groups.keys()].sort((a, b) => {
    if (a === 'Unassigned') return 1;
    if (b === 'Unassigned') return -1;
    return a.localeCompare(b);
  });

  return (
    <div className="space-y-4">
      <SectionHeading>Loan Board ({active.length})</SectionHeading>
      {sortedOfficerNames.map((officerName) => (
        <LoanOfficerSection key={officerName} officerName={officerName} loans={groups.get(officerName)} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Deadlines & Tasks (processor role — rule-based alerts computed from
// /loans-with-trackers. Manual user-created tasks are a second alert
// source planned for later; they will feed the same per-loan alert list
// via computeLoanAlerts rather than requiring a rework of this tab.)
// ---------------------------------------------------------------------------

// Same red/amber vocabulary as the Lock Expiry column (see
// lockExpiryUrgencyClass): urgent within URGENT_DAYS, approaching within
// APPROACHING_DAYS. Starting point to be tuned with processor feedback.
const URGENT_DAYS = 7;
const APPROACHING_DAYS = 14;

// Stalled-tracker alerts measure age since the tracker was ordered, not
// days until a deadline, so they get their own threshold pair -- also a
// starting point to be tuned with processor feedback.
const STALLED_APPROACHING_DAYS = 14;
const STALLED_URGENT_DAYS = 21;

const ALERT_SEVERITY_ORDER = { overdue: 0, urgent: 1, approaching: 2 };

const ALERT_SEVERITY_STYLES = {
  overdue: 'bg-red-500/10 text-red-400 border-red-500/30',
  urgent: 'bg-red-500/10 text-red-400 border-red-500/30',
  approaching: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
};

// A second, independent dimension alongside severity: WHAT KIND of
// attention an alert is asking for, not how urgent it is. STALE_FILE is
// something on the file that's gone out of date and needs re-checking
// (not a deadline in the usual sense); OPERATIONAL is an active deadline
// or in-progress step actually being worked. A third category,
// COMPLIANCE, covers CD timing and appraisal delivery and will be added
// in a later pass -- that's a matter of adding an entry here and rules
// that pass it, not reworking this constant, the factories, or the
// rendering below.
//
// Object key order IS the display/grouping priority DeadlinesTab uses.
const ALERT_CATEGORIES = {
  STALE_FILE: 'Needs Re-check',
  OPERATIONAL: 'In Progress',
};

function classifyDeadlineSeverity(daysUntilValue) {
  if (daysUntilValue === null) return null;
  if (daysUntilValue < 0) return 'overdue';
  if (daysUntilValue <= URGENT_DAYS) return 'urgent';
  if (daysUntilValue <= APPROACHING_DAYS) return 'approaching';
  return null;
}

function classifyStalledSeverity(ageDays) {
  if (ageDays === null) return null;
  if (ageDays > STALLED_URGENT_DAYS) return 'urgent';
  if (ageDays > STALLED_APPROACHING_DAYS) return 'approaching';
  return null;
}

// A date-driven rule fires when a named date on the loan falls inside the
// urgent/approaching window. category is taken explicitly rather than
// inferred from the factory, since two rules built by this same factory
// can land in different categories -- see CREDIT_EXPIRATION vs.
// LOAN_CONTINGENCY in ALERT_RULES below.
function makeDateAlertRule(id, label, dateName, category) {
  return {
    id,
    label,
    category,
    urgentDays: URGENT_DAYS,
    approachingDays: APPROACHING_DAYS,
    evaluate(loan) {
      const rawValue = getLoanDateValue(loan, dateName);
      const severity = classifyDeadlineSeverity(daysUntil(rawValue));
      if (!severity) return null;
      return { ruleId: id, label, category, severity, detail: formatDateWithDays(rawValue).label };
    },
  };
}

// A tracker+date rule factory (makeTrackerNotDoneRule) used to live here,
// firing when a tracker was still undone AND a named date fell inside the
// urgent/approaching window. Removed once its two rules (CD_NOT_SENT,
// FUNDING_NOT_ORDERED) were folded into makeClosingDatePassedRule above.
// The planned COMPLIANCE rules for CD timing and appraisal delivery will
// need a new factory rather than this one, since those deadlines are
// counted in business days against actual federal holiday dates, not the
// calendar-day math classifyDeadlineSeverity uses.

// A stalled-tracker rule fires when a tracker has sat in ORDERED status
// longer than STALLED_APPROACHING_DAYS.
function makeStalledTrackerRule(id, label, trackerName, category) {
  return {
    id,
    label,
    category,
    urgentDays: STALLED_URGENT_DAYS,
    approachingDays: STALLED_APPROACHING_DAYS,
    evaluate(loan) {
      const trackers = (loan && loan.trackers) || [];
      const entry = trackers.find((t) => t.trackerName === trackerName);
      if (!entry || (entry.status || '').toUpperCase() !== 'ORDERED') return null;
      const days = daysUntil(entry.trackerDate);
      if (days === null) return null;
      const severity = classifyStalledSeverity(-days);
      if (!severity) return null;
      return { ruleId: id, label, category, severity, detail: formatDateWithDays(entry.trackerDate).label };
    },
  };
}

// EARLIEST_CLOSING used to feed three separate rules -- CLOSING,
// CD_NOT_SENT, FUNDING_NOT_ORDERED -- so a single passed closing date
// produced three alerts carrying the identical date and severity, roughly
// a third of all alerts on real data. Collapsed into this one rule, which
// prompts a re-check of those things instead of repeating the same date
// three times.
//
// Reads CLOSING_CONTINGENCY, not EARLIEST_CLOSING. EARLIEST_CLOSING is a
// regulatory floor -- the earliest a loan could legally close given CD
// timing -- with no independent operational meaning of its own.
// CLOSING_CONTINGENCY is what Arive's own UI surfaces as the closing date
// (labelled "Est Closing" in the loan header) and what a processor
// actually works to; on loan 17317854 the two differed by a week, and
// alerting on EARLIEST_CLOSING reported 8 days overdue on a loan Arive
// showed closing the next day. The separate CLOSING_CONTINGENCY rule that
// used to live in ALERT_RULES was removed for the same reason the
// original three were collapsed -- it would otherwise fire on the exact
// same date as this rule, recreating the duplication this rule exists to
// eliminate. This is a judgment about processor workflow, not a fact
// about the data, and is subject to revision once processors are actually
// using this tab.
//
// Fires ONLY once CLOSING_CONTINGENCY is in the past -- an upcoming
// closing is the plan, not a deadline to chase, so a future date produces
// no alert at all here (unlike makeDateAlertRule, which also fires on an
// approaching date). Severity is always 'overdue': there is no
// "approaching" state for a date that, by definition, has to have already
// passed to fire. Written as its own factory rather than reusing
// makeDateAlertRule since both the severity rule and the purpose differ.
//
// EARLIEST_CLOSING itself is untouched elsewhere: it stays stored, stays
// in LOAN_KEY_DATES, and stays what LoanTable's CLOSING column reads --
// still worth seeing on a loan, just not worth alerting on.
function makeClosingDatePassedRule(id, label, category) {
  return {
    id,
    label,
    category,
    evaluate(loan) {
      const rawValue = getLoanDateValue(loan, 'CLOSING_CONTINGENCY');
      const days = daysUntil(rawValue);
      if (days === null || days >= 0) return null;
      return {
        ruleId: id,
        label,
        category,
        severity: 'overdue',
        detail: `${formatDateWithDays(rawValue).label} — re-check document age, rate lock, and CD accuracy`,
      };
    },
  };
}

// Every alert rule for the Deadlines tab. Adding or tuning a rule is an
// edit to this array only -- computing a loan's alerts is a map over it,
// not a chain of if-statements.
const ALERT_RULES = [
  makeClosingDatePassedRule('CLOSING_DATE_PASSED', 'Closing Date Passed', 'STALE_FILE'),
  makeDateAlertRule('LOCK_EXPIRY', 'Lock Expiry', 'LOCK_EXPIRY', 'OPERATIONAL'),
  makeDateAlertRule('CREDIT_EXPIRATION', 'Credit Expiration', 'CREDIT_EXPIRATION', 'STALE_FILE'),
  makeDateAlertRule('LOAN_CONTINGENCY', 'Loan Contingency', 'LOAN_CONTINGENCY', 'OPERATIONAL'),
  makeDateAlertRule('APPRAISAL_CONTINGENCY', 'Appraisal Contingency', 'APPRAISAL_CONTINGENCY', 'OPERATIONAL'),
  makeStalledTrackerRule('APPRAISAL_STALLED', 'Appraisal Stalled', 'APPRAISAL', 'OPERATIONAL'),
  makeStalledTrackerRule('TITLE_STALLED', 'Title Stalled', 'TITLE', 'OPERATIONAL'),
  makeStalledTrackerRule('HOI_STALLED', 'HOI Stalled', 'HOI', 'OPERATIONAL'),
];

// Rule-based alerts for one loan. Manual user-created tasks will become a
// second source feeding this same per-loan alert list -- concatenate their
// results here once that source exists, rather than reworking callers.
function computeLoanAlerts(loan) {
  return ALERT_RULES.map((rule) => rule.evaluate(loan)).filter(Boolean);
}

function sortBySeverity(a, b) {
  return ALERT_SEVERITY_ORDER[a.severity] - ALERT_SEVERITY_ORDER[b.severity];
}

// Groups loans that have at least one alert, each loan's alerts sorted
// worst-first, and the groups themselves sorted by their worst alert.
function buildDeadlineGroups(loans) {
  const groups = loans
    .map((loan) => ({ loan, alerts: computeLoanAlerts(loan).sort(sortBySeverity) }))
    .filter((group) => group.alerts.length > 0);
  groups.sort((a, b) => sortBySeverity(a.alerts[0], b.alerts[0]));
  return groups;
}

// Buckets one loan's already-computed alerts by category, in
// ALERT_CATEGORIES priority order, skipping any category with nothing in
// it, each bucket sorted worst-first. Purely a rendering concern -- it
// does not touch buildDeadlineGroups' own severity sort, which is what
// determines card order by the loan's single worst alert regardless of
// category.
function groupAlertsByCategory(alerts) {
  return Object.keys(ALERT_CATEGORIES)
    .map((category) => ({
      category,
      alerts: alerts.filter((alert) => alert.category === category).sort(sortBySeverity),
    }))
    .filter((group) => group.alerts.length > 0);
}

function AlertSeverityBadge({ severity }) {
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide border whitespace-nowrap ${ALERT_SEVERITY_STYLES[severity]}`}
    >
      {severity}
    </span>
  );
}

function DeadlinesTab() {
  const loans = useApiData('/loans-with-trackers');
  if (loans.loading) return <LoadingBlock label="Loading deadlines..." />;
  if (loans.error) return <ErrorBlock message={loans.error} onRetry={loans.retry} />;

  const active = (loans.data || []).filter((l) => isProcessorLoanStatus(l.status));
  const groups = buildDeadlineGroups(active);

  if (groups.length === 0) {
    return (
      <div className="space-y-4">
        <SectionHeading>Deadlines & Tasks</SectionHeading>
        <EmptyBlock message="No deadlines or stalled trackers. Everything is on track." />
      </div>
    );
  }

  const counts = { overdue: 0, urgent: 0, approaching: 0 };
  for (const group of groups) {
    for (const alert of group.alerts) counts[alert.severity] += 1;
  }
  const summary = `${counts.overdue} overdue, ${counts.urgent} urgent, ${counts.approaching} approaching`;

  return (
    <div className="space-y-4">
      <SectionHeading right={<span className="text-sm text-zinc-500">{summary}</span>}>
        Deadlines & Tasks
      </SectionHeading>
      {groups.map(({ loan, alerts }) => {
        const categoryGroups = groupAlertsByCategory(alerts);
        const showCategoryHeaders = categoryGroups.length > 1;
        return (
          <Card key={loan.id} className="p-5">
            <div className="flex items-baseline justify-between gap-3">
              <p className="font-medium text-zinc-100">{loan.borrowerName || '—'}</p>
              <p className="text-gold font-semibold">{formatCurrency(loan.loanAmount)}</p>
            </div>
            <p className="text-xs text-zinc-500 mb-3">
              {loan.loanNumber} • {loan.loanOfficerName || loan.loanOfficerEmail || 'Unassigned'}
            </p>
            <div className="space-y-3">
              {categoryGroups.map(({ category, alerts: categoryAlerts }) => (
                <div key={category}>
                  {showCategoryHeaders && (
                    <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">
                      {ALERT_CATEGORIES[category]}
                    </p>
                  )}
                  <ul className="space-y-2">
                    {categoryAlerts.map((alert) => (
                      <li key={alert.ruleId} className="flex items-center justify-between gap-3 text-sm">
                        <span className="text-zinc-300">{alert.label}</span>
                        <span className="flex items-center gap-2">
                          <span className="text-xs text-zinc-500">{alert.detail}</span>
                          <AlertSeverityBadge severity={alert.severity} />
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Card>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Goals (locally managed, persisted to localStorage)
// ---------------------------------------------------------------------------

const GOALS_KEY = 'kcd_goals_v1';
const DEFAULT_GOALS = [
  { id: 'g1', title: 'Monthly Loan Volume', target: 2000000, current: 1250000, unit: 'currency' },
  { id: 'g2', title: 'New Loans Closed', target: 20, current: 12, unit: 'count' },
  { id: 'g3', title: 'Candidate Pipeline', target: 10, current: 4, unit: 'count' },
];

function loadGoals() {
  try {
    const raw = localStorage.getItem(GOALS_KEY);
    return raw ? JSON.parse(raw) : DEFAULT_GOALS;
  } catch {
    return DEFAULT_GOALS;
  }
}

function GoalCard({ goal, onChange, onRemove }) {
  const pct = goal.target > 0 ? Math.min(100, Math.round((goal.current / goal.target) * 100)) : 0;
  const fmt = (v) => (goal.unit === 'currency' ? formatCurrency(v) : v);

  return (
    <Card className="p-5">
      <div className="flex items-start justify-between gap-2 mb-3">
        <input
          className="bg-transparent font-semibold text-zinc-100 border-b border-transparent hover:border-zinc-700 focus:border-gold outline-none flex-1"
          value={goal.title}
          onChange={(e) => onChange(goal.id, { title: e.target.value })}
        />
        <button onClick={() => onRemove(goal.id)} className="text-zinc-600 hover:text-red-400 text-xs shrink-0">
          Remove
        </button>
      </div>
      <div className="w-full h-2 bg-zinc-800 rounded-full overflow-hidden mb-2">
        <div className="h-full bg-gradient-to-r from-gold-dark to-gold" style={{ width: `${pct}%` }} />
      </div>
      <div className="flex items-center justify-between text-sm text-zinc-400 mb-3">
        <span>
          {fmt(goal.current)} / {fmt(goal.target)}
        </span>
        <span className="text-gold font-semibold">{pct}%</span>
      </div>
      <div className="flex gap-2">
        <label className="text-xs text-zinc-500 flex-1">
          Current
          <input
            type="number"
            value={goal.current}
            onChange={(e) => onChange(goal.id, { current: Number(e.target.value) })}
            className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded-md px-2 py-1 text-zinc-100 text-sm focus:border-gold outline-none"
          />
        </label>
        <label className="text-xs text-zinc-500 flex-1">
          Target
          <input
            type="number"
            value={goal.target}
            onChange={(e) => onChange(goal.id, { target: Number(e.target.value) })}
            className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded-md px-2 py-1 text-zinc-100 text-sm focus:border-gold outline-none"
          />
        </label>
      </div>
    </Card>
  );
}

function GoalsTab() {
  const [goals, setGoals] = useState(loadGoals);

  useEffect(() => {
    localStorage.setItem(GOALS_KEY, JSON.stringify(goals));
  }, [goals]);

  const updateGoal = (id, patch) => setGoals((gs) => gs.map((g) => (g.id === id ? { ...g, ...patch } : g)));
  const removeGoal = (id) => setGoals((gs) => gs.filter((g) => g.id !== id));
  const addGoal = () => {
    const title = window.prompt('New goal name?');
    if (!title) return;
    setGoals((gs) => [...gs, { id: `g${Date.now()}`, title, target: 100, current: 0, unit: 'count' }]);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <SectionHeading>Goals</SectionHeading>
        <button
          onClick={addGoal}
          className="px-3 py-1.5 rounded-md border border-gold/40 text-gold text-sm hover:bg-gold/10 transition"
        >
          + Add Goal
        </button>
      </div>
      {goals.length === 0 ? (
        <EmptyBlock message="No goals yet. Add one to start tracking progress." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {goals.map((g) => (
            <GoalCard key={g.id} goal={g} onChange={updateGoal} onRemove={removeGoal} />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Projects (locally managed, persisted to localStorage)
// ---------------------------------------------------------------------------

const PROJECTS_KEY = 'kcd_projects_v1';
const DEFAULT_PROJECTS = [
  { id: 'p1', name: 'Website Refresh', status: 'in-progress', notes: 'Homepage redesign in review with marketing.' },
  { id: 'p2', name: 'CRM Migration', status: 'on-hold', notes: 'Waiting on vendor contract signature.' },
  { id: 'p3', name: 'Branch Expansion — Denver', status: 'planning', notes: 'Site visit scheduled next month.' },
];
const PROJECT_STATUSES = ['planning', 'in-progress', 'on-hold', 'complete'];

function loadProjects() {
  try {
    const raw = localStorage.getItem(PROJECTS_KEY);
    return raw ? JSON.parse(raw) : DEFAULT_PROJECTS;
  } catch {
    return DEFAULT_PROJECTS;
  }
}

function ProjectsTab() {
  const [projects, setProjects] = useState(loadProjects);

  useEffect(() => {
    localStorage.setItem(PROJECTS_KEY, JSON.stringify(projects));
  }, [projects]);

  const update = (id, patch) => setProjects((ps) => ps.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  const remove = (id) => setProjects((ps) => ps.filter((p) => p.id !== id));
  const add = () => {
    const name = window.prompt('New project name?');
    if (!name) return;
    setProjects((ps) => [...ps, { id: `p${Date.now()}`, name, status: 'planning', notes: '' }]);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <SectionHeading>Projects</SectionHeading>
        <button
          onClick={add}
          className="px-3 py-1.5 rounded-md border border-gold/40 text-gold text-sm hover:bg-gold/10 transition"
        >
          + Add Project
        </button>
      </div>
      {projects.length === 0 ? (
        <EmptyBlock message="No active projects." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {projects.map((p) => (
            <Card key={p.id} className="p-5">
              <div className="flex items-start justify-between gap-2 mb-3">
                <input
                  value={p.name}
                  onChange={(e) => update(p.id, { name: e.target.value })}
                  className="bg-transparent font-semibold text-zinc-100 border-b border-transparent hover:border-zinc-700 focus:border-gold outline-none flex-1"
                />
                <button onClick={() => remove(p.id)} className="text-zinc-600 hover:text-red-400 text-xs shrink-0">
                  Remove
                </button>
              </div>
              <select
                value={p.status}
                onChange={(e) => update(p.id, { status: e.target.value })}
                className="mb-3 bg-zinc-900 border border-zinc-800 rounded-md px-2 py-1 text-sm text-zinc-200 focus:border-gold outline-none capitalize"
              >
                {PROJECT_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s.replace('-', ' ')}
                  </option>
                ))}
              </select>
              <textarea
                value={p.notes}
                onChange={(e) => update(p.id, { notes: e.target.value })}
                rows={3}
                placeholder="Notes..."
                className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-zinc-300 focus:border-gold outline-none resize-none"
              />
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// To-Do List
// ---------------------------------------------------------------------------

const PRIORITY_STYLES = {
  urgent: 'bg-red-500/10 text-red-400 border-red-500/30',
  important: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
  normal: 'bg-zinc-500/10 text-zinc-300 border-zinc-500/30',
};

// Inline editor for one list's group assignment. initialValue re-syncs
// whenever the grouped data is refetched (e.g. after this or another
// list's edit is saved), so a list that moved groups shows its new value
// on the next render without needing its own fetch.
function ListGroupControl({ listId, initialValue, onSaved }) {
  const [value, setValue] = useState(initialValue);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/todos/lists/${listId}/group`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ group_name: value }),
      });
      if (!res.ok) throw new Error('Failed to update group.');
      onSaved();
    } catch (err) {
      window.alert(err.message || 'Failed to update group.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-2 shrink-0">
      <input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Ungrouped"
        className="bg-zinc-900 border border-zinc-800 rounded-md px-2 py-1 text-xs text-zinc-200 focus:border-gold outline-none w-28"
      />
      <button
        onClick={save}
        disabled={saving}
        className="px-2 py-1 rounded-md border border-gold/40 text-gold text-xs hover:bg-gold/10 transition disabled:opacity-40"
      >
        {saving ? 'Saving...' : 'Save'}
      </button>
    </div>
  );
}

function TodoTab() {
  const todos = useApiData('/todos/grouped');
  const events = useApiData('/calendar');
  const [completingId, setCompletingId] = useState(null);

  const markComplete = async (id) => {
    setCompletingId(id);
    try {
      const res = await fetch(`${API_BASE}/todos/${id}`, { method: 'PUT' });
      if (!res.ok) throw new Error('Failed to update task.');
      todos.retry();
    } catch (err) {
      window.alert(err.message || 'Failed to mark task complete.');
    } finally {
      setCompletingId(null);
    }
  };

  if (todos.loading) return <LoadingBlock label="Loading tasks..." />;
  if (todos.error) return <ErrorBlock message={todos.error} onRetry={todos.retry} />;

  const groups = todos.data || [];
  const totalTasks = groups.reduce((sum, g) => sum + g.lists.reduce((s, l) => s + l.tasks.length, 0), 0);

  // "Upcoming" means a timed event that hasn't started yet (exact
  // instant, unchanged), OR an all-day event whose corrected calendar
  // date hasn't finished yet -- comparing an all-day event's raw stored
  // instant (always midnight UTC) against the current instant would drop
  // it out of "upcoming" hours before its own day is over, the same
  // wrong-day failure mode as everywhere else in this file.
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const upcomingEvents = (events.data || [])
    .filter((ev) => {
      if (!ev.startTime) return false;
      if (ev.allDay) {
        const d = eventCompareDate(ev);
        return d && d >= startOfToday;
      }
      return new Date(ev.startTime) >= new Date();
    })
    .sort((a, b) => eventCompareDate(a) - eventCompareDate(b))
    .slice(0, 5);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <SectionHeading>Open Tasks ({totalTasks})</SectionHeading>
        {totalTasks === 0 ? (
          <EmptyBlock message="Nothing on your plate. Nice work." />
        ) : (
          groups.map((group) => (
            <div key={group.group_name} className="space-y-3">
              <h3
                className={`text-xs font-semibold uppercase tracking-[0.2em] ${
                  group.group_name === 'Ungrouped' ? 'text-zinc-600' : 'text-gold'
                }`}
              >
                {group.group_name}
              </h3>
              <div className="space-y-3">
                {group.lists.map((list) => (
                  <Card key={list.list_id} className="p-4">
                    <div className="flex items-center justify-between gap-2 mb-3 pb-3 border-b border-zinc-800">
                      <p className="text-sm font-semibold text-zinc-200 truncate">{list.list_name || 'Untitled list'}</p>
                      <ListGroupControl
                        listId={list.list_id}
                        initialValue={group.group_name === 'Ungrouped' ? '' : group.group_name}
                        onSaved={todos.retry}
                      />
                    </div>
                    <div className="divide-y divide-zinc-800">
                      {list.tasks.map((t) => (
                        <div key={t.id} className="flex items-center gap-3 py-2 first:pt-0 last:pb-0">
                          <button
                            onClick={() => markComplete(t.id)}
                            disabled={completingId === t.id}
                            aria-label="Mark complete"
                            className="h-5 w-5 rounded border-2 border-zinc-600 hover:border-gold shrink-0 disabled:opacity-40 transition"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-zinc-100 truncate">{t.task}</p>
                            {t.dueDate && <p className="text-xs text-zinc-500">Due {formatDate(t.dueDate)}</p>}
                          </div>
                          <span
                            className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-medium border capitalize whitespace-nowrap ${
                              PRIORITY_STYLES[t.priority] || PRIORITY_STYLES.normal
                            }`}
                          >
                            {t.priority}
                          </span>
                        </div>
                      ))}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      <div>
        <SectionHeading>From Calendar</SectionHeading>
        <Card className="p-4">
          {events.error ? (
            <p className="text-sm text-zinc-500">Calendar unavailable.</p>
          ) : upcomingEvents.length === 0 ? (
            <EmptyBlock message="No upcoming events." />
          ) : (
            <ul className="space-y-3">
              {upcomingEvents.map((ev) => {
                const rowContent = (
                  <>
                    <p className="text-gold font-mono text-xs">{formatEventDateTime(ev)}</p>
                    <p className="text-zinc-200 break-words">{ev.title}</p>
                  </>
                );
                return (
                  <li key={ev.id} className="text-sm border-b border-zinc-800 last:border-0 pb-2 last:pb-0">
                    {ev.webLink ? (
                      <a
                        href={ev.webLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="contents hover:text-gold transition"
                      >
                        {rowContent}
                      </a>
                    ) : (
                      rowContent
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// WiseHire
// ---------------------------------------------------------------------------

const FIT_RATING_STYLES = {
  'great fit': { label: 'Great fit', className: 'bg-green-500/10 text-green-400 border-green-500/30' },
  'good fit': { label: 'Good fit', className: 'bg-gold/10 text-gold border-gold/30' },
  'low fit': { label: 'Low fit', className: 'bg-zinc-500/10 text-zinc-300 border-zinc-700' },
};

function getFitBadge(fitRating) {
  const normalized = (fitRating || '').toLowerCase();
  return FIT_RATING_STYLES[normalized] || { label: 'Unrated', className: 'bg-zinc-500/10 text-zinc-300 border-zinc-700' };
}

// WiseHire has no public per-candidate profile URL, so candidate cards link
// to the main WiseHire dashboard where the user locates the applicant.
// Same URL for the whole Apex WiseHire company account; if this ever needs
// to differ per tenant, lift it into config.
const WIZEHIRE_DASHBOARD_URL = 'https://wizehire.com/login';

function WiseHireTab() {
  const candidates = useApiData('/candidates');
  if (candidates.loading) return <LoadingBlock label="Loading recruiting pipeline..." />;
  if (candidates.error) return <ErrorBlock message={candidates.error} onRetry={candidates.retry} />;

  const list = candidates.data || [];
  const qualified = list.filter((c) => c.status === 'qualified');
  const belowBar = list.filter((c) => c.status === 'below_bar');
  const needsReview = list.filter((c) => c.status !== 'qualified' && c.status !== 'below_bar');

  const candidateCard = (c, { muted = false } = {}) => {
    const badge = getFitBadge(c.fitRating);
    return (
      <Card key={c.id} className={`p-5 ${muted ? 'opacity-75' : ''}`}>
        <div className="flex items-start justify-between mb-2 gap-2">
          <div className="min-w-0">
            <p className="font-semibold text-zinc-100 truncate">{c.name}</p>
            <p className="text-xs text-zinc-500 truncate">{c.position || 'Position TBD'}</p>
          </div>
          <span className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-semibold border ${badge.className}`}>
            {badge.label}
          </span>
        </div>
        <div className="text-xs text-zinc-500 space-y-1 mb-3">
          {c.email && <p className="truncate">{c.email}</p>}
          {c.phone && <p className="truncate">{c.phone}</p>}
        </div>
        {c.notes && <p className="text-sm text-zinc-400 mb-3 line-clamp-3">{c.notes}</p>}
        <a
          href={WIZEHIRE_DASHBOARD_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 pt-3 border-t border-zinc-800 block text-center text-xs text-zinc-500 hover:text-gold transition"
        >
          View in WiseHire
        </a>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <SectionHeading>WiseHire Recruiting Pipeline ({list.length})</SectionHeading>
      {list.length === 0 ? (
        <EmptyBlock message="No candidates in the pipeline yet." />
      ) : (
        <>
          <div className="space-y-4">
            <SectionHeading>Qualified ({qualified.length})</SectionHeading>
            {qualified.length === 0 ? (
              <EmptyBlock message="No qualified candidates yet." />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {qualified.map((c) => candidateCard(c))}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <SectionHeading>Needs review ({needsReview.length})</SectionHeading>
            {needsReview.length === 0 ? (
              <EmptyBlock message="Nothing needs review right now." />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {needsReview.map((c) => candidateCard(c))}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <SectionHeading>Below bar ({belowBar.length})</SectionHeading>
            {belowBar.length === 0 ? (
              <EmptyBlock message="No below-bar candidates." />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {belowBar.map((c) => candidateCard(c, { muted: true }))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Analytics (placeholder)
// ---------------------------------------------------------------------------

function AnalyticsTab() {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <div className="h-16 w-16 rounded-full border-2 border-gold/40 flex items-center justify-center mb-4">
        <ChartIcon />
      </div>
      <h2 className="text-xl font-semibold text-zinc-100 mb-2">Analytics</h2>
      <p className="text-zinc-500 max-w-sm">Coming soon. Deeper performance insights and reporting are on the way.</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

function Header({ tabs, activeTab, setActiveTab, mobileMenuOpen, setMobileMenuOpen, onRefresh, isRefreshing, lastUpdated }) {
  return (
    <header className="sticky top-0 z-40 bg-black/95 backdrop-blur border-b border-gold/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-9 w-9 shrink-0 rounded-md bg-gold/10 border border-gold/40 flex items-center justify-center text-gold font-bold">
              {appConfig.user.name ? appConfig.user.name.trim().charAt(0).toUpperCase() : 'U'}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white leading-none truncate">
                {appConfig.user.name ? `${appConfig.user.name}'s Command Dashboard` : 'Command Dashboard'}
              </p>
              <p className="text-[11px] text-zinc-500 leading-none mt-1 truncate">
                {appConfig.user.email || 'Executive Overview'}
                {appConfig.user.role && (
                  <span className="text-gold"> · {ROLE_LABELS[appConfig.user.role] || appConfig.user.role}</span>
                )}
              </p>
            </div>
          </div>

          <nav className="hidden lg:flex items-center gap-1 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-2.5 py-2 rounded-md text-[13px] font-medium transition whitespace-nowrap ${
                  activeTab === tab.id ? 'bg-gold/10 text-gold' : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3 shrink-0">
            <div className="hidden sm:block text-right">
              <p className="text-[11px] text-zinc-500">Last updated</p>
              <p className="text-xs text-zinc-300 font-mono">{lastUpdated.toLocaleTimeString()}</p>
            </div>
            <button
              onClick={onRefresh}
              disabled={isRefreshing}
              aria-label="Refresh"
              className="p-2 rounded-md border border-zinc-800 hover:border-gold text-zinc-300 hover:text-gold transition disabled:opacity-50"
            >
              <RefreshIcon spinning={isRefreshing} />
            </button>
            <button
              onClick={() => setMobileMenuOpen((o) => !o)}
              aria-label="Toggle navigation menu"
              className="lg:hidden p-2 rounded-md border border-zinc-800 text-zinc-300"
            >
              <MenuIcon open={mobileMenuOpen} />
            </button>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-zinc-800 bg-black px-4 py-3 space-y-1 max-h-[70vh] overflow-y-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setMobileMenuOpen(false);
              }}
              className={`block w-full text-left px-3 py-2 rounded-md text-sm font-medium transition ${
                activeTab === tab.id ? 'bg-gold/10 text-gold' : 'text-zinc-400 hover:bg-zinc-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t border-zinc-900 py-4 text-center text-xs text-zinc-600">
      {appConfig.user.name ? `${appConfig.user.name}'s Command Dashboard` : 'Command Dashboard'} • Internal Use Only
    </footer>
  );
}

function renderTab(id) {
  switch (id) {
    case 'briefing':
      return <BriefingRoomTab />;
    case 'calendar':
      return <CalendarTab />;
    case 'loans':
      return <ActiveLoansTab />;
    case 'loanboard':
      return <LoanBoardTab />;
    case 'deadlines':
      return <DeadlinesTab />;
    case 'pipeline':
      return <PipelineTab />;
    case 'goals':
      return <GoalsTab />;
    case 'projects':
      return <ProjectsTab />;
    case 'todos':
      return <TodoTab />;
    case 'wisehire':
      return <WiseHireTab />;
    case 'analytics':
      return <AnalyticsTab />;
    default:
      return null;
  }
}

function App() {
  // Roles with no entry in roles.js (or an unset/misconfigured USER_ROLE)
  // fail open to the full tab list rather than rendering a blank
  // dashboard — tab visibility is a convenience, not a security boundary.
  const roleTabIds = getVisibleTabs(appConfig.user.role);
  const visibleTabIds = roleTabIds.length > 0 ? roleTabIds : ALL_TABS;
  const visibleTabs = TABS.filter((tab) => visibleTabIds.includes(tab.id));

  const [activeTab, setActiveTab] = useState(visibleTabs[0]?.id || TABS[0].id);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const res = await fetch(`${API_BASE}/agent/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error(`Sync failed with status ${res.status}`);
      setRefreshKey((k) => k + 1);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Agent One refresh failed', err);
      window.alert('Sync failed — showing last saved data.');
      setRefreshKey((k) => k + 1);
      setLastUpdated(new Date());
    } finally {
      setIsRefreshing(false);
    }
  };

  const activeLabel = visibleTabs.find((t) => t.id === activeTab)?.label;

  return (
    <DashboardContext.Provider value={{ refreshKey }}>
      <div className="min-h-screen bg-black text-zinc-100 flex flex-col">
        <Header
          tabs={visibleTabs}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          mobileMenuOpen={mobileMenuOpen}
          setMobileMenuOpen={setMobileMenuOpen}
          onRefresh={handleRefresh}
          isRefreshing={isRefreshing}
          lastUpdated={lastUpdated}
        />
        <main className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-2xl font-bold text-white mb-6">{activeLabel}</h1>
          {renderTab(activeTab)}
        </main>
        <Footer />
      </div>
    </DashboardContext.Provider>
  );
}

export default App;
